import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import {
  FileText, AlertCircle,
  ArrowLeft, Loader2, Save, Trash2, GripVertical,
  Maximize2, Minimize2, Database, Pencil, Plus, X, ChevronRight, ChevronDown, ChevronUp,
  ChevronsDownUp, ChevronsUpDown, Filter, Table2, Eye,
  Sparkles, ArrowLeftRight, ChevronLeft, LayoutGrid, Palette,
  ChartColumn, ChartBar, ChartLine, ChartPie, ChartColumnStacked, ChartScatter,
  ChartNoAxesCombined, CreditCard, Gauge, CircleDot, Grid3x3, AlignEndHorizontal,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { TypeChip, eyebrowStyle } from '@/pages/workbench/analytics/shared'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  loadVisualObject, saveVisualObject,
  listVisualSchemas, loadVisualSchema, type VisualSchemaSummary,
  listBusinessAreas, loadBusinessArea, type BusinessAreaSummary,
} from '@/api/designers'
import { useProjectStore } from '@/store/projectStore'
import { useBuilderContextStore } from '@/store/builderContextStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentDeletion, useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useDesignHistory } from '@/hooks/useDesignHistory'
import UndoRedoButtons from '@/components/common/UndoRedoButtons'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import { MapBoundaryStatus } from '@/components/geo/MapBoundaries'
import SchemaDesignerPage from './SchemaDesignerPage'
import DocumentFormDesignerPage from './DocumentFormDesignerPage'
import { isDocumentVisual as isDocumentVisualType } from '@twasta/visual-engine'
import { DocumentOutputPanel } from './visual/DocumentOutputPanel'
import {
  VISUAL_CATALOG, loadVisualCatalog,
  type VisualCatalogEntry,
} from './visual/catalog'
import type {
  VisualModel, VisualColumn, CriteriaNode, CriteriaRule, CriteriaGroup,
  VisualOption, VisualSchema, ColumnGroup,
} from './visual/types'
import { isCriteriaGroup, criteriaLeaves, criteriaGroupAtPath } from './visual/types'
import {
  blankVisualModel, sanitizeVisualModel,
} from './visual/helpers'
import {
  availableColumns, groupByTable, bankKeyOf, materializeColumn,
  deriveSqlCriteria, schemaColumnToCriteria, syncMasterFromGroups, setSqlBinding,
  type BankColumn, type ResolvedSqlColumn,
} from './visual/dataSource'
import { parseSourceSql } from './visual/sqlParser'
import { fetchSqlColumns, fetchVisualFunctions, type VisualFunction } from '@/api/visualData'
import DisplayFormatEditor from './transaction/DisplayFormatEditor'
import ColourRulesEditor, { colourRuleEngine } from './visual/ColourRulesEditor'
import type { ColumnDef, DisplayFormat } from '@twasta/page-runtime/txn'
import SortTopNPanel from './visual/SortTopNPanel'
import FunctionCriteriaCard from './visual/FunctionCriteriaCard'
import CriteriaCard from './visual/CriteriaCard'
import VisualPreviewModal, { VisualInlinePreview } from './visual/VisualPreviewModal'
import type { VisualDataPayload } from '@twasta/visual-engine'
import { VDO_CSS } from './visual/designerShellCss'
// The query builder, inline above the chart in SQL mode (the same workspace
// every SQL field's "Query builder" window uses).
const QueryBuilderWorkspace = lazy(() => import('@/components/sqlbuilder/QueryBuilderWorkspace'))
import FieldTree from './visual/FieldTree'
import CalculationDialog, { type CalcDraft } from './visual/CalculationDialog'
import {
  CALC_KEY_PREFIX, calcBankColumns, calcInfoOf, calcsUsing, calculationsOf,
  compileCalculation, nextCalcKey, refreshCalcColumns, renameCalcRefs,
} from './visual/calculations'
import DropZones, { visibleGroups, type ZoneDrop } from './visual/DropZones'
import AxisShelves from './visual/AxisShelves'
import StyleOptions from './visual/StyleOptions'
import { useDragWidth } from './visual/useDragWidth'
import { slotIconToken } from './visual/slotIcons'
import {
  roleOf, toAutoField, chooseVisual, assignToGroups, suggestVisuals, type FieldRole,
  carryOver,
} from './visual/autoVisual'
import VisualTypePicker from './visual/VisualTypePicker'
import EventsPanel from './visual/EventsPanel'
import { newLayer, type EventsBlock } from '@twasta/visual-engine'
import { listProjectVariables, type ProjectVariable } from '@/api/projectVariables'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'

const COLTYPE_OPTIONS = ['CHAR', 'NUMBER', 'DATE'] as const
const TYPE_OPTIONS = ['string', 'number', 'date'] as const
// Fallback function list while the dialect catalog loads (or the endpoint is
// unreachable) — only functions every supported database runs.
const PORTABLE_FUNCTIONS: VisualFunction[] = [
  { name: 'SUM', label: 'Sum', kind: 'aggregate' },
  { name: 'AVG', label: 'Average', kind: 'aggregate' },
  { name: 'COUNT', label: 'Count', kind: 'aggregate' },
  { name: 'COUNT_DISTINCT', label: 'Count distinct', kind: 'aggregate' },
  { name: 'MIN', label: 'Minimum', kind: 'aggregate' },
  { name: 'MAX', label: 'Maximum', kind: 'aggregate' },
]

// Client-side pivot aggregations — mirrors ProPivot's ALL_AGGREGATIONS +
// AGGREGATION_CAPTIONS (packages/../ProPivot/src/core/aggregations.ts:131-155).
// Hardcoded locally so the designer main chunk never imports @proteus/propivot.
const PIVOT_AGG_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: '(default — from Aggregation)' },
  { value: 'sum', label: 'Sum' },
  { value: 'count', label: 'Count' },
  { value: 'distinctcount', label: 'Distinct Count' },
  { value: 'average', label: 'Average' },
  { value: 'median', label: 'Median' },
  { value: 'product', label: 'Product' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'percent', label: '% of Grand Total' },
  { value: 'percentofcolumn', label: '% of Column' },
  { value: 'percentofrow', label: '% of Row' },
  { value: 'index', label: 'Index' },
  { value: 'difference', label: 'Difference' },
  { value: '%difference', label: '% Difference' },
  { value: 'stdevp', label: 'Population StdDev' },
  { value: 'stdevs', label: 'Sample StdDev' },
  { value: 'runningtotals', label: 'Running Totals' },
]
// Positional family — honours a PIVOT_AXIS direction.
const POSITIONAL_PIVOT_AGGS = new Set(['difference', '%difference', 'runningtotals'])

type Selection =
  | { kind: 'none' }
  | { kind: 'column'; colId: number }

// Drag payloads carried across the Layout column bank ↔ groups and the
// schema tree ↔ criteria drop-zone.
type DragItem =
  | { kind: 'bank'; key: string }     // an unplaced data-source column
  | { kind: 'col'; colId: number }    // a column already placed in a group

/** Pull a VisualSchema out of whatever shape the loader returned. */
function extractSchema(content: unknown): VisualSchema | null {
  if (!content || typeof content !== 'object') return null
  const c = content as Record<string, unknown>
  const inner = (c.SCHEMA as Record<string, unknown>) || c
  const tables = Array.isArray(inner.TABLES) ? inner.TABLES : []
  return {
    SCHEMA_NAME: String(inner.SCHEMA_NAME || ''),
    SCHEMA_DESCR: String(inner.SCHEMA_DESCR || ''),
    SCHEMA_TYPE: String(inner.SCHEMA_TYPE || 'S'),
    CONNECTION_ID: String(inner.CONNECTION_ID || ''),
    TABLES: tables as VisualSchema['TABLES'],
    JOINS: Array.isArray(inner.JOINS) ? (inner.JOINS as VisualSchema['JOINS']) : [],
    COLUMN_GROUPS: Array.isArray(inner.COLUMN_GROUPS)
      ? (inner.COLUMN_GROUPS as VisualSchema['COLUMN_GROUPS'])
      : [],
  }
}

// Per-user designer preferences (drop-zone mode, Auto) — conveniences only:
// an unreadable store just means the defaults.
const PREF_PREFIX = 'twasta.visualDesigner.'
function readPref(key: string, fallback: string): string {
  try { return window.localStorage.getItem(PREF_PREFIX + key) ?? fallback } catch { return fallback }
}
function writePref(key: string, value: string) {
  try { window.localStorage.setItem(PREF_PREFIX + key, value) } catch { /* private window — keep in memory only */ }
}

/** Icons for the toolbar's quick picks (the full gallery is VisualTypePicker). */
const QUICK_ICONS: Record<string, LucideIcon> = {
  Column: ChartColumn, Bar: ChartBar, Line: ChartLine, Pie: ChartPie, 'Stacked Column': ChartColumnStacked,
  Scatter: ChartScatter, 'Column Line': ChartNoAxesCombined, Card: CreditCard, Gauge: Gauge, DoughNut: CircleDot,
  Heatmap: Grid3x3, Pivot: Grid3x3, Waterfall: AlignEndHorizontal,
}

/**
 * Switch a model to a catalog visual type: its ColumnGroups and options come
 * from the catalog; columns already placed carry over into the group with the
 * same StandardName (case-insensitive), the rest leave the layout.
 */
function applyCatalogEntry(m: VisualModel, entry: VisualCatalogEntry) {
  // Style settings the author CHANGED (value ≠ the old type's default) carry
  // over to an option of the same name, when the value is one the new type
  // accepts; everything else takes the new type's own default.
  const prevEntry = VISUAL_CATALOG.find((v) => v.VisualName === m.SQLMODEL.VISUAL_NAME)
  const prevDefault = new Map((prevEntry?.options || []).map((o) => [o.optionName, o.defaultValue ?? '']))
  const changedByName = new Map<string, string>()
  for (const o of m.LAYOUT_DATA.visualLayout.options || []) {
    const v = o.defaultValue ?? ''
    if (!prevEntry || (prevDefault.has(o.optionName) && prevDefault.get(o.optionName) !== v)) changedByName.set(o.optionName, v)
  }
  const carried = (o: VisualCatalogEntry['options'][number]): string => {
    if (!changedByName.has(o.optionName)) return o.defaultValue
    const v = changedByName.get(o.optionName) as string
    const lov = (o.listOfValues || '').split(',').map((x) => x.trim()).filter(Boolean)
    if (!lov.length) return o.dataType === 'number' && v !== '' && Number.isNaN(Number(v)) ? o.defaultValue : v
    // Same word, the new type's own spelling ("Blue" / "blue").
    return lov.find((x) => x.toLowerCase() === v.toLowerCase()) ?? (v === '' ? v : o.defaultValue)
  }
  m.SQLMODEL.VISUAL_NAME = entry.VisualName
  m.LAYOUT_DATA.visualLayout.ID = entry.ID
  m.LAYOUT_DATA.visualLayout.VisualName = entry.VisualName
  m.LAYOUT_DATA.visualLayout.VisualIcon = entry.VisualIcon
  m.LAYOUT_DATA.visualLayout.DisplayOrder = entry.DisplayOrder
  m.LAYOUT_DATA.visualLayout.className = entry.className
  m.LAYOUT_DATA.visualLayout.OutputType = entry.OutputType
  m.LAYOUT_DATA.visualLayout.linkOptions = ''
  const existingCols = new Map<string, VisualColumn[]>()
  for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    // Case-insensitive, as in helpers.ts: an app-server visual says "rows".
    existingCols.set(String(g.StandardName || '').toLowerCase(), g.COLUMNS || [])
  }
  m.LAYOUT_DATA.visualLayout.ColumnGroups = entry.ColumnGroups.map(
    (gs, i) => ({
      GroupID: i + 1,
      GroupName: gs.GroupName,
      GroupDescription: gs.GroupDescription,
      GroupIcon: gs.GroupIcon,
      AllowedColumnTypes: gs.AllowedColumnTypes,
      MinColumns: gs.MinColumns,
      MaxColumns: gs.MaxColumns,
      StandardName: gs.StandardName,
      COLUMNS: existingCols.get(gs.StandardName.toLowerCase()) || [],
    })
  )
  m.LAYOUT_DATA.visualLayout.options = entry.options.map((o) => ({
    optionId: o.optionId,
    optionName: o.optionName,
    optionHeading: o.optionHeading,
    dataType: o.dataType,
    defaultValue: carried(o),
    listOfValues: o.listOfValues,
    allowChange: o.allowChange,
    optionType: o.optionType,
    showWhen: o.showWhen,
  }))
  syncMasterFromGroups(m)
}

export default function VisualDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [model, setModel] = useState<VisualModel>(() => blankVisualModel(objectName))
  const [projectVars, setProjectVars] = useState<ProjectVariable[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [selection, setSelection] = useState<Selection>({ kind: 'none' })
  const [dragItem, setDragItem] = useState<DragItem | null>(null)
  // Data · Layout · Style (were Data Source · Layout · Options).
  const [tab, setTab] = useState<'data' | 'layout' | 'style'>('layout')
  const [panelOpen, setPanelOpen] = useState(true)
  // Inline query builder pane: its height and whether it is folded away are
  // per-person conveniences; where the TABLES sit is kept with the project.
  const [qbHeight, setQbHeight] = useState(() => Math.max(340, Number(readPref('qbHeight', '560')) || 560))
  const [qbHidden, setQbHidden] = useState(() => readPref('qbHidden', '0') === '1')
  const setQbHiddenPref = (h: boolean) => { setQbHidden(h); writePref('qbHidden', h ? '1' : '0') }
  const startQbResize = (e: React.PointerEvent) => {
    e.preventDefault()
    const sy = e.clientY, h0 = qbHeight
    let last = h0
    const move = (ev: PointerEvent) => { last = Math.min(1400, Math.max(340, h0 + ev.clientY - sy)); setQbHeight(last) }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      writePref('qbHeight', String(Math.round(last)))
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }
  // Drop zones: full, minimised to icon tiles, or hidden — remembered per user.
  const [wellMode, setWellModeState] = useState<'full' | 'mini' | 'hidden'>(() => {
    const v = readPref('wellMode', 'full')
    return v === 'mini' || v === 'hidden' ? v : 'full'
  })
  const setWellMode = useCallback((v: 'full' | 'mini' | 'hidden') => { setWellModeState(v); writePref('wellMode', v) }, [])
  // Auto visual selection — remembered per user, on unless turned off.
  const [auto, setAutoState] = useState<boolean>(() => readPref('auto', 'on') === 'on')
  const setAuto = useCallback((on: boolean) => { setAutoState(on); writePref('auto', on ? 'on' : 'off') }, [])
  const [autoNote, setAutoNote] = useState<{ visual: string; reason: string } | null>(null)
  // The live preview's last dataset — the Data tab's rows and query.
  const [payload, setPayload] = useState<VisualDataPayload | null>(null)
  // A date clicked on the preview → the Events panel's quick add.
  const [eventDatePick, setEventDatePick] = useState<{ date: string; label: string; rect: DOMRect | null } | null>(null)
  const [canvasHot, setCanvasHot] = useState(false)
  const [rowsOpen, setRowsOpen] = useState(false)
  const [queryOpen, setQueryOpen] = useState(false)
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  // Live preview of the current (possibly unsaved) model — rendered in the
  // shared, standalone VisualPreviewModal (outside the designer tree) so the
  // same preview surface is reused by the card "View" and app navigation.
  const [previewOpen, setPreviewOpen] = useState(false)

  // ── Visual schema (Schema-mode data source) ──────────────────────
  const [schemaList, setSchemaList] = useState<VisualSchemaSummary[]>([])
  const [schemaData, setSchemaData] = useState<VisualSchema | null>(null)
  const [schemaBusy, setSchemaBusy] = useState(false)
  // Business Areas — the analytics basis a visual can be built on.
  // 'Default' (blank) = no area: the visual reads the project DB / its own
  // schema exactly as before, with no area definition required.
  const [areaList, setAreaList] = useState<BusinessAreaSummary[]>([])
  // The Business Area's referenced schema name (when the area delegates its
  // dataset to a Visual Schema) — display-only.
  const [areaSchemaName, setAreaSchemaName] = useState('')
  const [editSchemaOpen, setEditSchemaOpen] = useState(false)
  // Bumped to force a schema reload (after the in-place editor closes).
  const [schemaNonce, setSchemaNonce] = useState(0)
  // The document designer, opened in place exactly as the schema designer is.
  const [editDocumentOpen, setEditDocumentOpen] = useState(false)
  const [documentNonce, setDocumentNonce] = useState(0)

  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(model) !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)
  const history = useDesignHistory(model, setModel, { disabled: !canEdit })
  const { reset: resetHistory } = history

  const agentRefresh = useAgentRefresh(projectName, isDirty)

  // An agent run that DELETED this object must close it, not refetch it — a
  // refetch 404s and leaves the removed object on screen (and a later Save
  // would resurrect it). Deletions only became visible to the client in Phase
  // 0.8 of docs/ssh_agent_console_support_plan.md.
  useAgentDeletion(projectName, objectName, 'visual_models', () => {
    if (embedded) return   // the host tab owns navigation
    navigate('/workbench/analytics')
  })

  // Publish the open visual as the Builder/Ask-AI "open object" so the agent
  // resolves "this visual" / "this chart" / "this" / "here" to it instead of
  // guessing — parity with the nav tree and workflow designer.
  useEffect(() => {
    if (!objectName) return
    useBuilderContextStore.getState().setOpenObject({
      obj_name: objectName,
      obj_type: 'V',
      title: objectName,
    })
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [objectName])

  useEffect(() => {
    if (!projectName) { setProjectVars([]); return }
    let cancelled = false
    listProjectVariables(projectName)
      .then((vars) => { if (!cancelled) setProjectVars(vars) })
      .catch(() => { if (!cancelled) setProjectVars([]) })
    return () => { cancelled = true }
  }, [projectName])

  // Schema catalogue (names) — for the Data Source schema picker dropdown.
  useEffect(() => {
    if (!projectName) { setSchemaList([]); return }
    let cancelled = false
    listVisualSchemas(projectName)
      .then((res) => { if (!cancelled) setSchemaList(res?.schemas || []) })
      .catch(() => { if (!cancelled) setSchemaList([]) })
    return () => { cancelled = true }
  }, [projectName, schemaNonce])

  // Business Area catalogue — for the "Based on" selector.
  useEffect(() => {
    if (!projectName) { setAreaList([]); return }
    let cancelled = false
    listBusinessAreas(projectName)
      .then((res) => { if (!cancelled) setAreaList(res?.business_areas || []) })
      .catch(() => { if (!cancelled) setAreaList([]) })
    return () => { cancelled = true }
  }, [projectName])

  // Load the visual catalog + the visual model, then sanitize.
  useEffect(() => {
    let cancelled = false
    if (!projectName || !objectName) return
    setLoading(true)
    Promise.all([
      loadVisualCatalog(),
      loadVisualObject(projectName, objectName).catch((err) => {
        console.error('Failed to load visual:', err)
        toast.error('Failed to load visual')
        return null
      }),
    ])
      .then(([, data]) => {
        if (cancelled) return
        const loaded = data && data.content
          ? sanitizeVisualModel(data.content as VisualModel, objectName)
          : blankVisualModel(objectName)
        // SQL mode: reconcile criteria with the SQL's ?. placeholders on open
        // so the panel reflects the query without waiting for an edit. Snapshot
        // is taken afterwards, so this silent normalization isn't "dirty".
        if ((loaded.DATA_SOURCE_MODE || 'sql') === 'sql') {
          loaded.SQLMODEL.CRITERIA.query.rules = deriveSqlCriteria(
            loaded.SOURCE_SQL || '', loaded.SQLMODEL.CRITERIA.query.rules,
          ).rules
        }
        setModel(loaded)
        setSavedSnapshot(JSON.stringify(loaded))
        // A visual with no data source yet opens on Data; otherwise on Layout.
        const hasSource = (loaded.DATA_SOURCE_MODE || 'sql') === 'sql'
          ? !!(loaded.SOURCE_SQL || '').trim()
          : !!(loaded.SQLMODEL.SCHEMA_NAME || loaded.SQLMODEL.BUSINESS_AREA)
        setTab(hasSource ? 'layout' : 'data')
        // A (re)load replaces the model wholesale — stale undo entries would
        // restore a different/older visual.
        resetHistory()
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectName, objectName, agentRefresh, resetHistory])

  // Load the dataset (tables/columns) when in schema mode. With a Business
  // Area selected the dataset comes from the AREA — its referenced Visual
  // Schema when set, else the area's own tables/joins (the area body reuses
  // the schema shape). Without an area (Default) the visual's own schema
  // reference loads exactly as before.
  const schemaName = model.SQLMODEL.SCHEMA_NAME || ''
  const businessArea = (model.SQLMODEL.BUSINESS_AREA || '').trim()
  const mode = model.DATA_SOURCE_MODE || 'sql'
  useEffect(() => {
    if (mode !== 'sqlmodel' || !projectName || (!schemaName && !businessArea)) {
      setSchemaData(null)
      setAreaSchemaName('')
      return
    }
    let cancelled = false
    setSchemaBusy(true)
    const loadDataset = async (): Promise<VisualSchema | null> => {
      if (businessArea) {
        const res = await loadBusinessArea(projectName, businessArea)
        const raw = res?.content as Record<string, unknown> | null
        const inner = (raw?.BUSINESS_AREA as Record<string, unknown>) || raw
        if (!inner) return null
        const ref = String(inner.SCHEMA_NAME || '').trim()
        if (!cancelled) setAreaSchemaName(ref)
        if (ref) {
          const s = await loadVisualSchema(projectName, ref)
          if (s?.content) return extractSchema(s.content)
        }
        return inner as unknown as VisualSchema
      }
      if (!cancelled) setAreaSchemaName('')
      const res = await loadVisualSchema(projectName, schemaName)
      return res?.content ? extractSchema(res.content) : null
    }
    loadDataset()
      .then((data) => { if (!cancelled) setSchemaData(data) })
      .catch(() => { if (!cancelled) setSchemaData(null) })
      .finally(() => { if (!cancelled) setSchemaBusy(false) })
    return () => { cancelled = true }
  }, [mode, projectName, schemaName, businessArea, schemaNonce])

  const catalogEntry: VisualCatalogEntry = useMemo(() => {
    return (
      VISUAL_CATALOG.find((c) => c.VisualName === model.SQLMODEL.VISUAL_NAME) ||
      VISUAL_CATALOG[0]
    )
  }, [model.SQLMODEL.VISUAL_NAME])

  /**
   * This visual's output is a laid-out DOCUMENT rather than a chart.
   *
   * Read from `className`, which is what every renderer already dispatches on
   * — the catalog's `OutputType` is stored but has never been consulted at
   * render time, and making this the one place it suddenly mattered would put
   * two answers in the tree to the same question.
   */
  const isDocumentVisual = isDocumentVisualType(
    catalogEntry.className, catalogEntry.ID)

  // ── Mutators ────────────────────────────────────────────────
  const updateModel = useCallback(
    (mutator: (m: VisualModel) => void) => {
      setModel((prev) => {
        const next = JSON.parse(JSON.stringify(prev)) as VisualModel
        mutator(next)
        return next
      })
    },
    []
  )

  // Mode toggle. Switching to SQL re-derives criteria from the SQL; switching
  // to schema keeps any visually-built rules.
  const handleModeChange = useCallback(
    (next: 'sql' | 'sqlmodel') => {
      updateModel((m) => {
        m.DATA_SOURCE_MODE = next
        if (next === 'sql') {
          const { rules } = deriveSqlCriteria(m.SOURCE_SQL || '', m.SQLMODEL.CRITERIA.query.rules)
          m.SQLMODEL.CRITERIA.query.rules = rules
        } else {
          // A schema visual has no query of its own — the engine builds one
          // from the schema. Keeping the old text would leave a query nothing
          // in the designer shows, and the app server RUNS a stored SOURCE_SQL
          // in preference to the schema, so a deploy would answer with the
          // visual's previous question.
          m.SOURCE_SQL = ''
        }
        // Layout placements may reference the other source — clear them so the
        // bank reflects the new data source cleanly.
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) g.COLUMNS = []
        syncMasterFromGroups(m)
      })
      setSelection({ kind: 'none' })
    },
    [updateModel]
  )

  const handleSourceSqlChange = useCallback(
    (value: string) => {
      updateModel((m) => {
        m.SOURCE_SQL = value
        if ((m.DATA_SOURCE_MODE || 'sql') === 'sql') {
          const { rules } = deriveSqlCriteria(value, m.SQLMODEL.CRITERIA.query.rules)
          m.SQLMODEL.CRITERIA.query.rules = rules
        }
      })
    },
    [updateModel]
  )

  const handleSchemaNameChange = useCallback(
    (name: string) => {
      // Keep the author's casing — schema files are case-sensitive on the
      // server (the file name IS the schema id). Only strip unsafe chars.
      const clean = name.trim().replace(/[^A-Za-z0-9_-]/g, '_')
      updateModel((m) => {
        const summary = schemaList.find(
          (s) => s.schema_name.toUpperCase() === clean.toUpperCase()
        )
        // Picking a known schema (any casing) snaps to its exact stored name.
        m.SQLMODEL.SCHEMA_NAME = summary ? summary.schema_name : clean
        if (summary) m.SQLMODEL.SCHEMA_DESCR = summary.schema_descr
        // New schema → clear stale placements.
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) g.COLUMNS = []
        syncMasterFromGroups(m)
      })
      setSelection({ kind: 'none' })
    },
    [updateModel, schemaList]
  )

  const handleBusinessAreaChange = useCallback(
    async (name: string) => {
      // The Business Area drives the data source: an area WITH a schema
      // attached snaps the visual to Schema mode on that schema; an area
      // WITHOUT one starts from SQL Query (freehand SQL on the area's
      // connection). Default keeps whatever the author had.
      let refSchema = ''
      if (name) {
        try {
          const res = await loadBusinessArea(projectName, name)
          const raw = res?.content as Record<string, unknown> | null
          const inner = (raw?.BUSINESS_AREA as Record<string, unknown>) || raw
          refSchema = String(inner?.SCHEMA_NAME || '').trim()
        } catch { /* area unreadable — leave the mode unchanged */ }
      }
      updateModel((m) => {
        m.SQLMODEL.BUSINESS_AREA = name
        if (name) {
          if (refSchema) {
            m.DATA_SOURCE_MODE = 'sqlmodel'
            m.SQLMODEL.SCHEMA_NAME = refSchema
          } else {
            m.DATA_SOURCE_MODE = 'sql'
          }
        }
        // The dataset changed → stale placements would reference old columns.
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) g.COLUMNS = []
        syncMasterFromGroups(m)
      })
      setSelection({ kind: 'none' })
    },
    [updateModel, projectName]
  )

  // ── Column placement (Layout) ─────────────────────────────────
  const moveColumn = useCallback(
    (colId: number, targetStdName: string | null) => {
      updateModel((m) => {
        let moving: VisualColumn | undefined
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
          const found = (g.COLUMNS || []).find((c) => c.COLID === colId)
          if (found) { moving = found; g.COLUMNS = g.COLUMNS.filter((c) => c.COLID !== colId) }
        }
        if (moving && targetStdName) {
          const target = (m.LAYOUT_DATA.visualLayout.ColumnGroups || []).find(
            (g) => g.StandardName === targetStdName
          )
          if (target) {
            moving.groupName = target.GroupName
            moving.StandardName = target.StandardName
            target.COLUMNS = [...(target.COLUMNS || []), moving]
          }
        }
        syncMasterFromGroups(m)
      })
      if (!targetStdName) setSelection({ kind: 'none' })
    },
    [updateModel]
  )

  const handleColumnFieldChange = useCallback(
    (colId: number, field: keyof VisualColumn, value: unknown) => {
      updateModel((m) => {
        for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
          const col = (g.COLUMNS || []).find((c) => c.COLID === colId)
          if (col) (col as Record<string, unknown>)[field as string] = value
        }
        syncMasterFromGroups(m)
      })
    },
    [updateModel]
  )

  const findGroupForColumn = useCallback(
    (colId: number): string | null => {
      for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
        if ((g.COLUMNS || []).some((c) => c.COLID === colId)) return g.StandardName
      }
      return null
    },
    [model]
  )

  // ── Criteria ──────────────────────────────────────────────────
  const addSchemaCriteria = useCallback(
    (bankKey: string, bank: BankColumn[], groupPath: number[] | null = null) => {
      const src = bank.find((b) => b.key === bankKey)
      if (!src) return
      updateModel((m) => {
        const nodes = m.SQLMODEL.CRITERIA.query.rules
        const leaves = criteriaLeaves(nodes)
        // The SAME column may be dropped more than once — e.g. a range built
        // from two criteria (TRAN_DATE >= <prompt> AND TRAN_DATE < <fixed>).
        // The engine disambiguates the prompt names (unique_prompt_name); we
        // just need a unique rule id (across groups too).
        let n = leaves.length + 1
        while (leaves.some((r) => r.id === `Criteria_${n}`)) n += 1
        const rule = schemaColumnToCriteria(src, n)
        const dupCount = leaves.filter(
          (r) => (r.field || '').toUpperCase() === rule.field.toUpperCase()
        ).length
        // A second drop of the same column gets a distinguishing label so the
        // two prompts don't render with identical headings.
        if (dupCount > 0) rule.promptLabel = `${rule.promptLabel} (${dupCount + 1})`
        // Dropped onto a condition group (at any nesting depth) → the rule
        // joins that group; otherwise it lands top-level.
        const target = groupPath && groupPath.length
          ? criteriaGroupAtPath(nodes, groupPath) : null
        if (target) target.rules.push(rule)
        else nodes.push(rule)
      })
    },
    [updateModel]
  )

  // SQL mode only — its criteria list is always FLAT (derived from the ?.var
  // placeholders), so a plain index addresses the rule.
  const handleCriteriaChange = useCallback(
    (idx: number, field: keyof CriteriaRule, value: unknown) => {
      updateModel((m) => {
        const rule = m.SQLMODEL.CRITERIA.query.rules[idx]
        if (!rule || isCriteriaGroup(rule)) return
        ;(rule as Record<string, unknown>)[field as string] = value
      })
    },
    [updateModel]
  )

  // SQL mode: the prompt's data type + default live in the SQL placeholder
  // itself (insight-bi binding `?.name.<type>.<default>`). Editing them rewrites
  // the SOURCE_SQL, then re-derives the criteria so the card stays in sync.
  const handleSqlBinding = useCallback(
    (varName: string, type: 'string' | 'number' | 'date' | undefined, defaultValue: string | undefined) => {
      updateModel((m) => {
        m.SOURCE_SQL = setSqlBinding(m.SOURCE_SQL || '', varName, type, defaultValue)
        m.SQLMODEL.CRITERIA.query.rules = deriveSqlCriteria(
          m.SOURCE_SQL, m.SQLMODEL.CRITERIA.query.rules,
        ).rules
      })
    },
    [updateModel]
  )


  const handleOptionChange = useCallback(
    (optionId: string, value: string) => {
      updateModel((m) => {
        const opt = (m.LAYOUT_DATA.visualLayout.options || []).find((o) => o.optionId === optionId)
        if (opt) opt.defaultValue = value
      })
    },
    [updateModel]
  )

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !objectName) return false
    setSaving(true)
    try {
      await saveVisualObject(projectName, objectName, model)
      setSavedSnapshot(JSON.stringify(model))
      toast.success('Visual saved')
      // Tell the embedding shell the object now exists on disk: its Live
      // Preview tab is gated on that, answered once when the node was
      // selected, so a first save would otherwise leave the tab missing
      // until the node was reselected.
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save visual')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, model, onSaved])

  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  // ── SQL mode: real result columns, straight from the database ─
  // The SELECT-list parse cannot expand `SELECT *` — it would offer a literal
  // `*` chip. When the SQL uses a star we ask the server what the query
  // actually returns (read-only, one row) and use THAT as the bank. Debounced,
  // because this fires while the author is still typing.
  const sourceSql = model.SOURCE_SQL || ''
  const connectionId = model.SQLMODEL?.DATABASE_DETAIL?.CONNECTION_ID || ''
  // The inline query builder shows while Data is the current tab on a
  // SQL-mode visual the person may edit (a read-only viewer keeps the plain
  // text). Collapsing the panel keeps it — that is how it gets the full width;
  // its own fold button tucks it away.
  const inlineQb = tab === 'data' && mode === 'sql' && canEdit && !!projectName
  const sqlHasStar = useMemo(() => parseSourceSql(sourceSql).hasStar, [sourceSql])
  const [sqlCols, setSqlCols] =
    useState<{ sql: string; columns: ResolvedSqlColumn[] } | null>(null)
  const [sqlColsStatus, setSqlColsStatus] =
    useState<{ busy: boolean; error: string }>({ busy: false, error: '' })
  // The request needs the WHOLE model (connection / business area resolution),
  // but the effect must not re-fire on every unrelated keystroke — read the
  // current model through a ref and depend only on what changes the result.
  const modelRef = useRef(model)
  modelRef.current = model

  // ── Dialect-aware function catalog (Aggregation dropdown, ranking, and
  // function conditions). Resolved server-side from the visual's ACTUAL data
  // source (connection / schema / business area → preview DB), so the picker
  // only offers what the bound database can run (e.g. MEDIAN on Oracle/
  // Dremio but not SQLite; STDEV spelling handled by the engine).
  const schemaNameDep = model.SQLMODEL?.SCHEMA_NAME || ''
  const businessAreaDep = model.SQLMODEL?.BUSINESS_AREA || ''
  const [fnCatalog, setFnCatalog] = useState<VisualFunction[]>(PORTABLE_FUNCTIONS)
  useEffect(() => {
    if (!projectName) return
    let cancelled = false
    fetchVisualFunctions({ project: projectName, model: modelRef.current })
      .then((res) => {
        if (!cancelled && Array.isArray(res.functions) && res.functions.length) {
          setFnCatalog(res.functions)
        }
      })
      .catch(() => { /* keep the portable fallback list */ })
    return () => { cancelled = true }
  }, [projectName, schemaNameDep, businessAreaDep, connectionId])

  useEffect(() => {
    if (mode !== 'sql' || !sqlHasStar || !sourceSql.trim() || !projectName) {
      setSqlCols(null)
      setSqlColsStatus({ busy: false, error: '' })
      return
    }
    let cancelled = false
    setSqlColsStatus({ busy: true, error: '' })
    const timer = setTimeout(() => {
      fetchSqlColumns({ project: projectName, model: modelRef.current })
        .then((res) => {
          if (cancelled) return
          setSqlCols({ sql: sourceSql, columns: res.columns || [] })
          setSqlColsStatus({ busy: false, error: '' })
        })
        .catch((err: unknown) => {
          if (cancelled) return
          const detail = (err as { response?: { data?: { detail?: string } } })
            ?.response?.data?.detail
          setSqlCols(null)
          setSqlColsStatus({
            busy: false,
            error: detail || (err as Error)?.message
              || 'Could not read the query’s columns',
          })
        })
    }, 700)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [mode, sqlHasStar, sourceSql, connectionId, businessArea, projectName])

  // ── Derived column bank + placement state ─────────────────────
  // The data source's own fields …
  const baseBank = useMemo(
    () => availableColumns(
      model, schemaData,
      sqlCols && sqlCols.sql === sourceSql ? sqlCols.columns : null,
    ),
    [model, schemaData, sqlCols, sourceSql],
  )
  // … plus the visual's calculations (schema mode: they compile into the
  // query the engine builds; a SQL-mode query is the author's own SELECT).
  const calcs = useMemo(() => calculationsOf(model), [model])
  const bank = useMemo(
    () => (mode === 'sqlmodel' && calcs.length ? [...calcBankColumns(calcs, baseBank), ...baseBank] : baseBank),
    [mode, calcs, baseBank],
  )
  const [calcDraft, setCalcDraft] = useState<CalcDraft | null>(null)
  const [pendingPlace, setPendingPlace] = useState<string | null>(null)

  // A placed calculation keeps the SQL it was saved with. When the data source
  // changes under it (a schema column renamed), bring that SQL up to date; one
  // that no longer compiles keeps its last SQL and is flagged in the tree.
  useEffect(() => {
    if (mode !== 'sqlmodel' || !calcs.length || !baseBank.length) return
    const stale = (model.LAYOUT_DATA.visualLayout.ColumnGroups || []).some((g) => (g.COLUMNS || []).some((c) => {
      const info = calcInfoOf(c)
      const calc = info && calcs.find((x) => x.key === info.key)
      if (!info || !calc) return false
      const res = compileCalculation(calc, baseBank, calcs)
      return res.ok && (res.compiled.expression !== c.expression || res.compiled.fn !== c.FUNCTION
        || JSON.stringify(res.compiled.refs) !== JSON.stringify(info.refs))
    }))
    if (stale) updateModel((m) => { refreshCalcColumns(m, baseBank); syncMasterFromGroups(m) })
  }, [mode, calcs, baseBank, model.LAYOUT_DATA.visualLayout.ColumnGroups, updateModel])

  // Schema mode: option-referenced columns (group-by / trend / breakup / gauge)
  // that aren't placed on a card slot must still be SELECTed so their values
  // reach the renderer. Inject any missing ones into the master column list as
  // hidden columns. (SQL mode already exposes every SELECT alias, so this is a
  // no-op there.) Converges: once injected they're present, so it stops.
  useEffect(() => {
    if ((model.DATA_SOURCE_MODE || 'sql') !== 'sqlmodel') return
    const referenced = new Set<string>()
    for (const o of model.LAYOUT_DATA.visualLayout.options || []) {
      if (o.optionType !== 'column' && o.optionType !== 'column-multi') continue
      for (const nm of (o.defaultValue || '').split(',').map((s) => s.trim()).filter(Boolean)) {
        referenced.add(nm.toUpperCase())
      }
    }
    if (!referenced.size) return
    const master = model.SQLMODEL.SQLModel.COLUMNS?.[0]?.COLUMN || []
    const have = new Set(master.map((c) => (c.NAME || '').toUpperCase()))
    const toAdd = [...referenced]
      .filter((n) => !have.has(n))
      .map((n) => bank.find((b) => (b.name || '').toUpperCase() === n))
      .filter((b): b is BankColumn => !!b)
    if (!toAdd.length) return
    updateModel((m) => {
      const cols = m.SQLMODEL.SQLModel.COLUMNS?.[0]?.COLUMN || []
      let nextId = cols.length ? Math.max(...cols.map((c) => c.COLID || 0), 1) + 1 : 2
      for (const b of toAdd) {
        const col = materializeColumn(b, nextId++)
        ;(col as Record<string, unknown>).HIDDEN = true
        cols.push(col)
      }
      m.SQLMODEL.SQLModel.COLUMNS = [{ COLUMN: cols }]
    })
  }, [model, bank, updateModel])

  // ════════════════════════════════════════════════════════════
  // Canvas-first shell (docs/plans/visual-designer-canvas-first.md):
  // field tree + drop zones + live preview with field chips on the axes.
  // ════════════════════════════════════════════════════════════
  const groups = model.LAYOUT_DATA.visualLayout.ColumnGroups || []
  // Events on the timeline (docs/visual_events.md): a type supports them when
  // its catalog entry has an Events drop zone — data, not a type list.
  const isEventsZone = (std: string) => /^events$/i.test(std || '')
  const bankByKey = useMemo(() => new Map(bank.map((b) => [b.key, b])), [bank])
  const allCols = useMemo(() => groups.flatMap((g) => g.COLUMNS || []), [groups])
  const roleOfCol = useCallback((c: VisualColumn): FieldRole => {
    const b = bankByKey.get(bankKeyOf(c, mode))
    if (b) return roleOf(b)
    if (c.EXPRESSIONTYPE === 'G' || (c.FUNCTION || '').trim()) return 'measure'
    const t = (c.type || '').toLowerCase()
    return t === 'date' ? 'date' : t === 'number' ? 'measure' : 'dimension'
  }, [bankByKey, mode])
  // bank key → the zone it sits in, and the placed keys in zone order.
  const placedIn = useMemo(() => {
    const m = new Map<string, string>()
    for (const g of groups) for (const c of g.COLUMNS || []) m.set(bankKeyOf(c, mode), g.GroupName)
    return m
  }, [groups, mode])
  const placedOrder = useMemo(
    () => groups.flatMap((g) => (g.COLUMNS || []).map((c) => bankKeyOf(c, mode))).filter((k) => bankByKey.has(k)),
    [groups, mode, bankByKey],
  )
  const iconOf = useCallback((g: ColumnGroup) => {
    const spec = catalogEntry.ColumnGroups.find(
      (s) => s.StandardName.toLowerCase() === (g.StandardName || '').toLowerCase())
    return slotIconToken(spec || g)
  }, [catalogEntry])
  const colLabel = useCallback((c: VisualColumn) => c.content || c.NAME || `Column ${c.COLID}`, [])
  const colAgg = useCallback((c: VisualColumn) => (
    calcInfoOf(c) ? 'ƒx' : c.EXPRESSIONTYPE === 'G' ? (c.FUNCTION || c.DEFAULTFUNCTION || 'SUM') : ''
  ), [])
  const colGlyph = useCallback((c: VisualColumn) => {
    const r = roleOfCol(c)
    return r === 'measure' ? { text: '#', cls: 'vdo-g-num' } : r === 'date' ? { text: '31', cls: 'vdo-g-date' }
      : r === 'geo' ? { text: '⌖', cls: 'vdo-g-geo' } : { text: 'Aa', cls: 'vdo-g-dim' }
  }, [roleOfCol])
  const colDot = useCallback((c: VisualColumn) => {
    const r = roleOfCol(c)
    return r === 'measure' ? 'var(--color-accent)' : r === 'date' ? '#E07B39' : '#94a3b8'
  }, [roleOfCol])
  const canDrop = useCallback((): ZoneDrop => ({ ok: !!dragItem && canEdit }), [dragItem, canEdit])

  // ── Calculations ─────────────────────────────────────────────
  const openCalc = useCallback((calcKey: string | null) => {
    const c = calcKey ? calcs.find((x) => x.key === calcKey) : null
    setCalcDraft(c ? { key: c.key, label: c.label, formula: c.formula, when: c.when }
      : { key: '', label: '', formula: '', when: 'total' })
  }, [calcs])
  /** A calculation that cannot run is never placed silently: say why and open it. */
  const refuseBrokenCalc = useCallback((bankKey: string): boolean => {
    const b = bankByKey.get(bankKey)
    if (!b?.calcKey || b.calcCompiled) return false
    toast.error(`${b.label} can’t be added yet — ${b.calcProblem || 'its formula has a problem'}`)
    openCalc(b.calcKey)
    return true
  }, [bankByKey, openCalc])
  const saveCalc = useCallback((d: CalcDraft, placeNow: boolean): string => {
    if (!canEdit) return 'You can’t change this visual.'
    const res = compileCalculation(d, baseBank, calcs)
    if (!res.ok) return res.problem.message
    const key = d.key || nextCalcKey(calcs)
    const before = calcs.find((x) => x.key === key)
    updateModel((m) => {
      const list = calculationsOf(m)
      const rec = { key, label: d.label, formula: d.formula, when: d.when }
      const i = list.findIndex((x) => x.key === key)
      if (i >= 0) list[i] = rec
      else list.push(rec)
      if (before && before.label !== d.label) renameCalcRefs(list, key, before.label, d.label)
      m.SQLMODEL.CALCULATIONS = list
      refreshCalcColumns(m, baseBank)
      syncMasterFromGroups(m)
    })
    setCalcDraft(null)
    if (placeNow) setPendingPlace(`${CALC_KEY_PREFIX}${key}`)
    else toast.success(d.key ? `${d.label} updated` : `${d.label} added to Calculations`)
    return ''
  }, [canEdit, baseBank, calcs, updateModel])
  const deleteCalc = useCallback((calcKey: string): string => {
    if (!canEdit) return 'You can’t change this visual.'
    const target = calcs.find((x) => x.key === calcKey)
    if (!target) return ''
    const users = calcsUsing(calcs, target)
    if (users.length) {
      return `Can’t delete — ${users.map((x) => x.label).join(', ')} ${users.length === 1 ? 'uses' : 'use'} it. Change ${users.length === 1 ? 'that' : 'those'} first.`
    }
    updateModel((m) => {
      m.SQLMODEL.CALCULATIONS = calculationsOf(m).filter((x) => x.key !== calcKey)
      refreshCalcColumns(m, baseBank)
      syncMasterFromGroups(m)
    })
    setCalcDraft(null)
    setSelection({ kind: 'none' })
    toast.success(`${target.label} deleted`)
    return ''
  }, [canEdit, calcs, baseBank, updateModel])

  /** Put a field (a bank column, or a placed column by COLID) into a zone,
   *  optionally before another column. A zone at its field limit hands its
   *  last field back to where the incoming one came from (a swap), else
   *  drops it. A field the zone's type does not suit is placed with a note. */
  const placeInto = useCallback((src: { bankKey?: string; colId?: number }, std: string, beforeColId?: number) => {
    if (!canEdit) return
    const target = groups.find((g) => g.StandardName === std)
    if (!target) return
    const isSrc = (c: VisualColumn) => (src.colId != null ? c.COLID === src.colId : bankKeyOf(c, mode) === src.bankKey)
    const fromG = groups.find((g) => (g.COLUMNS || []).some(isSrc))
    const moving = fromG ? (fromG.COLUMNS || []).find(isSrc) : undefined
    if (!moving && !(src.bankKey && bankByKey.has(src.bankKey))) return
    if (!moving && refuseBrokenCalc(src.bankKey!)) return
    const role = moving ? roleOfCol(moving) : roleOf(bankByKey.get(src.bankKey!)!)
    const max = Number(target.MaxColumns || 0)
    const others = (target.COLUMNS || []).filter((c) => !isSrc(c))
    const displaced = max && others.length >= max ? others.slice(max - 1) : []
    const swapTo = fromG && fromG.StandardName !== std ? fromG : null
    updateModel((m) => {
      const gs = m.LAYOUT_DATA.visualLayout.ColumnGroups || []
      const t = gs.find((g) => g.StandardName === std)
      if (!t) return
      const match = (c: VisualColumn) => (src.colId != null ? c.COLID === src.colId : bankKeyOf(c, m.DATA_SOURCE_MODE || 'sql') === src.bankKey)
      let col: VisualColumn | undefined
      for (const g of gs) {
        const hit = (g.COLUMNS || []).find(match)
        if (hit) { col = hit; g.COLUMNS = g.COLUMNS.filter((c) => c !== hit) }
      }
      if (!col) {
        const b = src.bankKey ? bankByKey.get(src.bankKey) : undefined
        if (!b) return
        const ids = gs.flatMap((g) => (g.COLUMNS || []).map((c) => c.COLID || 0))
        col = materializeColumn(b, ids.length ? Math.max(...ids, 1) + 1 : 2)
      }
      let list = t.COLUMNS || []
      if (max && list.length >= max) {
        const out = list.slice(max - 1)
        list = list.slice(0, max - 1)
        const back = swapTo ? gs.find((g) => g.StandardName === swapTo.StandardName) : null
        if (back) {
          for (const d of out) { d.groupName = back.GroupName; d.StandardName = back.StandardName }
          back.COLUMNS = [...(back.COLUMNS || []), ...out]
        }
      }
      col.groupName = t.GroupName
      col.StandardName = t.StandardName
      if (isEventsZone(t.StandardName)) {
        // A text field must not split the line's grain: the catalog's
        // DefaultFunction (MAX) folds it to one value per date.
        const spec = catalogEntry.ColumnGroups.find((g) => g.StandardName === t.StandardName)
        const dfn = (spec as { DefaultFunction?: string } | undefined)?.DefaultFunction || ''
        if (dfn && (m.DATA_SOURCE_MODE || 'sql') === 'sqlmodel' && !(col.FUNCTION || '').trim()) {
          col.FUNCTION = dfn
          col.DEFAULTFUNCTION = dfn
          col.EXPRESSIONTYPE = 'G'
        }
        const vl = m.LAYOUT_DATA.visualLayout
        const ev = (vl.events && Array.isArray(vl.events.layers)) ? vl.events : { layers: [] }
        if (!ev.layers.some((l) => l.kind === 'column')) {
          const nl = newLayer('column', ev.layers, col.content || col.NAME || 'Events')
          vl.events = { layers: [...ev.layers, nl] }
        }
      }
      const at = beforeColId != null ? list.findIndex((c) => c.COLID === beforeColId) : -1
      t.COLUMNS = at >= 0 ? [...list.slice(0, at), col, ...list.slice(at)] : [...list, col]
      syncMasterFromGroups(m)
    })
    const name = moving ? colLabel(moving) : (bankByKey.get(src.bankKey!)?.label || 'The field')
    if (displaced.length) {
      toast(swapTo
        ? `Swapped ${name} with ${displaced.map(colLabel).join(', ')}`
        : `${name} replaced ${displaced.map(colLabel).join(', ')} — ${target.GroupName} holds ${max}`)
    }
    const kind = (target.AllowedColumnTypes || 'ANY').toUpperCase()
    if ((kind === 'NUMBER' && role !== 'measure' && role !== 'geo') || (kind === 'DATE' && role !== 'date')) {
      toast(`${target.GroupName} expects ${kind === 'NUMBER' ? 'a number — set its aggregation to Count if it is text' : 'a date'}.`)
    }
    setAutoNote(null)
  }, [canEdit, groups, mode, bankByKey, roleOfCol, updateModel, colLabel, refuseBrokenCalc, catalogEntry])

  const catalogNames = useMemo(() => VISUAL_CATALOG.map((v) => v.VisualName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogEntry])
  /** Auto: pick the visual for these fields and lay them into its zones. */
  const applyAuto = useCallback((keys: string[]) => {
    // The Events zone is the author's own choice — Auto never fills or empties it.
    const eventKeys = new Set(groups.filter((g) => isEventsZone(g.StandardName))
      .flatMap((g) => (g.COLUMNS || []).map((c) => bankKeyOf(c, mode))))
    const fields = keys.filter((k) => !eventKeys.has(k)).map((k) => bankByKey.get(k)).filter((b): b is BankColumn => !!b).map(toAutoField)
    if (!fields.length) return
    const choice = chooseVisual(fields, catalogNames, model.SQLMODEL.VISUAL_NAME)
    const entry = VISUAL_CATALOG.find((v) => v.VisualName === choice.visual) || catalogEntry
    const plan = assignToGroups(entry.ColumnGroups, fields)
    updateModel((m) => {
      const md = m.DATA_SOURCE_MODE || 'sql'
      const existing = new Map<string, VisualColumn>()
      for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
        for (const c of g.COLUMNS || []) existing.set(bankKeyOf(c, md), c)
      }
      const keptEvents = (m.LAYOUT_DATA.visualLayout.ColumnGroups || [])
        .filter((g) => isEventsZone(g.StandardName)).flatMap((g) => g.COLUMNS || [])
      applyCatalogEntry(m, entry)
      const ids = [...existing.values()].map((c) => c.COLID || 0)
      let next = ids.length ? Math.max(...ids, 1) + 1 : 2
      for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
        if (isEventsZone(g.StandardName)) {
          g.COLUMNS = keptEvents.map((c) => ({ ...c, groupName: g.GroupName, StandardName: g.StandardName }))
          continue
        }
        g.COLUMNS = []
        for (const key of plan[g.StandardName] || []) {
          const b = bankByKey.get(key)
          if (!b) continue
          const col = existing.get(key) || materializeColumn(b, next++)
          col.groupName = g.GroupName
          col.StandardName = g.StandardName
          g.COLUMNS.push(col)
        }
      }
      syncMasterFromGroups(m)
    })
    setSelection({ kind: 'none' })
    setAutoNote(choice.reason ? { visual: entry.VisualName, reason: choice.reason } : null)
  }, [bankByKey, catalogNames, catalogEntry, model.SQLMODEL.VISUAL_NAME, updateModel, groups, mode])

  /** The zone a field goes to when no zone was named (Auto off). */
  const bestZone = useCallback((role: FieldRole): string | null => {
    const room = (g: ColumnGroup) => { const max = Number(g.MaxColumns || 0); return !max || (g.COLUMNS || []).length < max }
    const numZone = (g: ColumnGroup) => /value|size|stack|lines|target|achievement/i.test(g.StandardName)
    const fits = (g: ColumnGroup) => !isEventsZone(g.StandardName)
      && (role === 'measure' ? numZone(g) : !numZone(g) && !/^(min|max)$/i.test(g.StandardName))
    const pick = groups.find((g) => fits(g) && room(g) && !(g.COLUMNS || []).length)
      || groups.find((g) => fits(g) && room(g)) || groups.find((g) => room(g) && !isEventsZone(g.StandardName)) || groups[0]
    return pick ? pick.StandardName : null
  }, [groups])
  const addField = useCallback((key: string) => {
    if (!canEdit || placedIn.has(key) || !bankByKey.has(key)) return
    if (refuseBrokenCalc(key)) return
    if (auto) { applyAuto([...placedOrder, key]); return }
    const std = bestZone(roleOf(bankByKey.get(key)!))
    if (std) placeInto({ bankKey: key }, std)
  }, [canEdit, placedIn, bankByKey, auto, applyAuto, placedOrder, bestZone, placeInto, refuseBrokenCalc])
  // "Add it to the visual now": the new calculation is placed once the bank
  // (re-derived from the saved model) has it.
  useEffect(() => {
    if (!pendingPlace || !bankByKey.has(pendingPlace)) return
    const key = pendingPlace
    setPendingPlace(null)
    if (placedIn.has(key)) return
    addField(key)
    toast.success(`${bankByKey.get(key)?.label || 'The calculation'} added to the visual`)
  }, [pendingPlace, bankByKey, placedIn, addField])
  const removeField = useCallback((key: string) => {
    if (!canEdit) return
    const rest = placedOrder.filter((k) => k !== key)
    if (auto && rest.length) { applyAuto(rest); return }
    const col = allCols.find((c) => bankKeyOf(c, mode) === key)
    if (col) moveColumn(col.COLID, null)
    setAutoNote(null)
  }, [canEdit, placedOrder, auto, applyAuto, allCols, mode, moveColumn])
  /** A drop on a zone / axis chip — whatever is being dragged. */
  const dropOnZone = useCallback((std: string, beforeColId?: number) => {
    if (dragItem?.kind === 'bank') placeInto({ bankKey: dragItem.key }, std, beforeColId)
    else if (dragItem?.kind === 'col') placeInto({ colId: dragItem.colId }, std, beforeColId)
    setDragItem(null)
  }, [dragItem, placeInto])
  const hasRC = groups.some((g) => /^rows$/i.test(g.StandardName)) && groups.some((g) => /^columns$/i.test(g.StandardName))
  const swapRowsColumns = useCallback(() => {
    updateModel((m) => {
      const gs = m.LAYOUT_DATA.visualLayout.ColumnGroups || []
      const r = gs.find((g) => /^rows$/i.test(g.StandardName)), c = gs.find((g) => /^columns$/i.test(g.StandardName))
      if (!r || !c) return
      const rc = r.COLUMNS || [], cc = c.COLUMNS || []
      r.COLUMNS = cc.map((x) => ({ ...x, groupName: r.GroupName, StandardName: r.StandardName }))
      c.COLUMNS = rc.map((x) => ({ ...x, groupName: c.GroupName, StandardName: c.StandardName }))
      syncMasterFromGroups(m)
    })
  }, [updateModel])
  /** Switch the visual type. Same drop zones ⇒ nothing moves; different
   *  zones ⇒ the fields are laid out as Auto would (`carryOver`). */
  const pickVisual = useCallback((name: string) => {
    if (!canEdit || name === model.SQLMODEL.VISUAL_NAME) return
    const entry = VISUAL_CATALOG.find((v) => v.VisualName === name)
    if (!entry) { toast.error(`${name} is not in the visual catalog.`); return }
    const placed = groups.flatMap((g) => (g.COLUMNS || []).map((c) => ({
      std: g.StandardName,
      field: { key: String(c.COLID), label: colLabel(c), role: roleOfCol(c) },
    })))
    const { plan, dropped } = carryOver(entry.ColumnGroups, placed, groups.map((g) => g.StandardName))
    updateModel((m) => {
      const byId = new Map<string, VisualColumn>()
      for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
        for (const c of g.COLUMNS || []) byId.set(String(c.COLID), c)
      }
      applyCatalogEntry(m, entry)
      for (const g of m.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
        g.COLUMNS = (plan[g.StandardName] || [])
          .map((k) => byId.get(k))
          .filter((c): c is VisualColumn => !!c)
          .map((c) => ({ ...c, groupName: g.GroupName, StandardName: g.StandardName }))
      }
      syncMasterFromGroups(m)
    })
    const zoneName = (std: string) => entry.ColumnGroups.find((g) => g.StandardName === std)?.GroupName || std
    const moved = placed.filter((p) => {
      const now = Object.keys(plan).find((std) => plan[std].includes(p.field.key))
      return now && now.toLowerCase() !== p.std.toLowerCase()
    }).map((p) => `${p.field.label} → ${zoneName(Object.keys(plan).find((std) => plan[std].includes(p.field.key))!)}`)
    const notes: string[] = []
    if (auto) { setAuto(false); notes.push(`Auto is off — you chose ${name}.`) }
    if (moved.length) notes.push(`Moved ${moved.join(', ')}.`)
    if (dropped.length) notes.push(`${dropped.map((f) => f.label).join(', ')} ${dropped.length === 1 ? 'has' : 'have'} no place in ${name} and left the visual.`)
    if (notes.length) toast(notes.join(' '), { duration: dropped.length ? 6000 : 4000 })
    setSelection({ kind: 'none' })
    setAutoNote(null)
  }, [canEdit, model.SQLMODEL.VISUAL_NAME, groups, colLabel, roleOfCol, updateModel, auto])
  // Quick picks come from the fields alone, so they keep their places when one
  // is clicked; the current type shows as pressed.
  const quick = useMemo(() => suggestVisuals(
    placedOrder.map((k) => bankByKey.get(k)).filter((b): b is BankColumn => !!b).map(toAutoField), catalogNames,
  ).slice(0, 3), [placedOrder, bankByKey, catalogNames])

  // One settings-panel width for all three tabs, and the Fields list's share
  // of it on Layout — both set by dragging a divider and remembered per
  // browser; the chart keeps at least 380 px.
  const bodyRef = useRef<HTMLDivElement>(null)
  const panelMax = useCallback(() => (bodyRef.current?.clientWidth ?? window.innerWidth) - 32 - 16 - 380, [])
  const panelSize = useDragWidth('panelWidth', 640, 340, panelMax, 'Settings panel width')
  // The drop zones keep room for at least one icon tile (they turn into tiles
  // by themselves when narrow).
  const fieldsMax = useCallback(() => panelSize.width - 84, [panelSize.width])
  const fieldsSize = useDragWidth('fieldsWidth', 280, 190, fieldsMax, 'Fields list width')

  // Style tab inputs: the catalog defaults (what "changed" is measured
  // against) and every column the option column-pickers may name.
  const optionDefaults = useMemo(
    () => new Map(catalogEntry.options.map((o) => [o.optionName, o.defaultValue ?? ''])), [catalogEntry])
  const optionColumns = useMemo(() => bank.map((b) => ({ name: b.name, label: b.label || b.name })), [bank])
  const liveOptions = model.LAYOUT_DATA.visualLayout.options
  const mapNotice = useMemo(() => {
    const by = new Map((liveOptions || []).map((o) => [o.optionName, o.defaultValue ?? '']))
    // A choropleth draws shapes from downloaded boundary files — show whether
    // THIS visual's country is available, and let the author fetch it here.
    return (by.get('mapMode') || '').toLowerCase() === 'choropleth'
      ? <MapBoundaryStatus level={by.get('geoLevel') || 'auto'} country={by.get('geoCountry') || ''} />
      : null
  }, [liveOptions])

  // The live preview runs on a debounced copy of the model — every edit
  // redraws, but a burst of typing costs one query. A change of visual type
  // is one deliberate click, so it goes straight through.
  const [previewModel, setPreviewModel] = useState<VisualModel>(model)
  useEffect(() => {
    const typeChanged = model.SQLMODEL.VISUAL_NAME !== previewModel.SQLMODEL.VISUAL_NAME
    const t = setTimeout(() => setPreviewModel(model), typeChanged ? 0 : 600)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model])
  // "Drawing…" cue: an edit waiting for the debounce, or a fetch in flight.
  const [previewFetching, setPreviewFetching] = useState(false)
  // Busy = a fetch in flight (or a type change not yet handed over). An
  // ordinary edit waiting for the debounce is NOT busy — dimming the chart on
  // every keystroke would flicker. The CSS only shows the cue after 250 ms.
  const previewBusy = previewFetching || model.SQLMODEL.VISUAL_NAME !== previewModel.SQLMODEL.VISUAL_NAME
  // When a fetch finishes the new chart fades in (the old one stayed on screen,
  // dimmed, until then — no blank flash, no blocking overlay).
  // Only when the visual TYPE changed — an option toggle or a keystroke
  // redraws in place, with no animation.
  const [previewEntering, setPreviewEntering] = useState(false)
  const wasFetching = useRef(false)
  const drawnType = useRef<string | null>(null)
  useEffect(() => {
    const finished = wasFetching.current && !previewFetching
    wasFetching.current = previewFetching
    if (!finished) return
    const type = previewModel.SQLMODEL.VISUAL_NAME
    const changed = drawnType.current !== null && drawnType.current !== type
    drawnType.current = type
    if (!changed) return
    setPreviewEntering(true)
    const t = setTimeout(() => setPreviewEntering(false), 700)
    return () => clearTimeout(t)
  }, [previewFetching, previewModel])
  // Only run the preview on a copy that has fields — the copy taken before the
  // visual finished loading would ask the server for an empty query.
  const previewReady = useMemo(
    () => (previewModel.LAYOUT_DATA?.visualLayout?.ColumnGroups || []).some((g) => (g.COLUMNS || []).length > 0),
    [previewModel],
  )
  const openEditor = useCallback((colId: number) => {
    setSelection({ kind: 'column', colId })
    setTab('layout'); setPanelOpen(true)
    if (wellMode !== 'full') setWellMode('full')
  }, [wellMode, setWellMode])

  // ── Render ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading visual…</span>
      </div>
    )
  }

  const sourceEmptyHint = mode === 'sqlmodel'
    ? (!schemaData ? 'Pick a schema or business area in the Data tab — its fields appear here.' : 'No columns found in the data source.')
    : (!(model.SOURCE_SQL || '').trim() ? 'Write a SQL query in the Data tab — its SELECT columns appear here.' : 'No columns found in the query.')
  const condTables = mode === 'sqlmodel' ? (schemaData?.TABLES || []).filter((t) => (t.CONDITION || '').trim()) : []
  const eventsGroup = groups.find((g) => isEventsZone(g.StandardName))
  const eventsField = eventsGroup && (eventsGroup.COLUMNS || [])[0] ? { label: colLabel(eventsGroup.COLUMNS[0]) } : null
  // The names a `?.name` may use — matched like the engine does: promptVar,
  // then field, then id.
  const eventPromptNames = criteriaLeaves(model.SQLMODEL?.CRITERIA?.query?.rules || [])
    .flatMap((r) => [r.promptVar, (r as { field?: string }).field, r.id])
    .map((v) => String(v || '')).filter(Boolean)
  const eventsPanel = eventsGroup ? (
    <EventsPanel
      events={model.LAYOUT_DATA.visualLayout.events}
      onChange={(next: EventsBlock) => updateModel((m) => {
        if (next.layers.length) m.LAYOUT_DATA.visualLayout.events = next
        else delete m.LAYOUT_DATA.visualLayout.events
      })}
      readOnly={!canEdit}
      project={projectName}
      model={model}
      target={connectionId ? `conn:${connectionId}` : 'project'}
      eventsField={eventsField}
      fieldChoices={bank.filter((b) => roleOf(b) === 'dimension').map((b) => ({ key: b.key, label: b.label, hint: b.tableLabel }))}
      onPickField={(key) => placeInto({ bankKey: key }, eventsGroup.StandardName)}
      onClearField={() => { for (const c of eventsGroup.COLUMNS || []) moveColumn(c.COLID, null) }}
      promptNames={eventPromptNames}
      resolved={((payload?.visualLayout as { events?: EventsBlock } | undefined)?.events) ?? null}
      addRequest={eventDatePick}
      onAddRequestDone={() => setEventDatePick(null)}
    />
  ) : null
  const layoutFooter = (
    <>
      {eventsPanel}
      {isDocumentVisual && (
        <div style={{ marginTop: 14 }}>
          <DocumentOutputPanel
            project={projectName}
            objName={objectName}
            model={model}
            nonce={documentNonce}
            // The document designer reads the visual from DISK, so an unsaved
            // column would be missing from the panel it is about to lay out.
            onSaveFirst={handleSave}
            onOpenDesigner={() => setEditDocumentOpen(true)}
          />
        </div>
      )}
      {condTables.length > 0 && (
        <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 12, background: 'var(--color-warning-weak)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--color-warning)', marginBottom: 4 }}>Filters injected from the schema</div>
          {condTables.map((t) => (
            <div key={t.TABLE_NAME} title={`${t.TABLE_NAME}: ${t.CONDITION}`}
              style={{ fontFamily: MONO_FONT, fontSize: 12.5, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.TABLE_NAME}: {t.CONDITION}
            </div>
          ))}
        </div>
      )}
    </>
  )
  const rowsTable = (() => {
    const jd = payload?.jsonData as unknown as Array<Record<string, unknown>> | undefined
    if (!jd || jd.length < 1) return null
    const cols = Object.keys(jd[0] || {})
    return { cols, rows: jd.slice(1, 61), total: Math.max(0, jd.length - 1) }
  })()
  const shelfProps = {
    className: catalogEntry.className,
    groups: visibleGroups(groups),
    iconOf, readOnly: !canEdit, dragging: !!dragItem, canDrop,
    colLabel, colAgg, colDot,
    onDrop: dropOnZone,
    onDragStartCol: (colId: number) => setDragItem({ kind: 'col', colId }),
    onDragEnd: () => setDragItem(null),
    onEdit: openEditor,
  }
  const draggingNewField = dragItem?.kind === 'bank' && !placedIn.has(dragItem.key)

  return (
    <div className={`vdo-root${panelSize.dragging || fieldsSize.dragging ? ' vdo-resizing' : ''}`} style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000,
    } : undefined}>
      <style>{VDX_CSS}</style>
      <style>{VDO_CSS}</style>

      {/* ── Top bar: title · visual toolbar · save ── */}
      <div className="vdo-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '1 1 220px', minWidth: 0 }}>
          {!embedded && (
            <button className="vdo-ib vdo-float" style={{ borderRadius: 11 }} onClick={handleBack} title="Back to workbench" aria-label="Back to workbench">
              <ArrowLeft size={16} />
            </button>
          )}
          <TypeChip code="V" size={22} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-faint)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{projectName} · Visual</div>
            <div style={{ fontSize: 16, fontWeight: 650, letterSpacing: '-0.2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{objectName}</div>
          </div>
        </div>

        <div className="vdo-float vdo-toolbar" role="toolbar" aria-label="Visual">
          <button className="vdo-auto" aria-pressed={auto} disabled={!canEdit}
            onClick={() => { const on = !auto; setAuto(on); if (on && placedOrder.length) applyAuto(placedOrder); else setAutoNote(null) }}
            title="Auto picks and adapts the visual as you add or remove fields">
            <Sparkles size={15} /> Auto <span className="trk" aria-hidden="true" />
          </button>
          {/* The visual type — the catalog's browsable picker. */}
          <VisualTypePicker value={model.SQLMODEL.VISUAL_NAME} onChange={pickVisual} />
          {quick.length > 0 && <span className="vdo-sep" />}
          {quick.map((s) => {
            const Icon = QUICK_ICONS[s.visual] || ChartColumn
            const on = s.visual === model.SQLMODEL.VISUAL_NAME
            return (
              <button key={s.visual} className={`vdo-ib${on ? ' on' : ''}`} disabled={!canEdit} onClick={() => pickVisual(s.visual)}
                aria-pressed={on} title={on ? `${s.visual} — the current type` : `${s.visual} — ${s.why}`}
                aria-label={`${s.visual} — ${s.why}`}>
                <Icon size={18} />{!on && <span className="vdo-dot" />}
              </button>
            )
          })}
          <span className="vdo-sep" />
          <button className="vdo-ib" onClick={swapRowsColumns} disabled={!canEdit || !hasRC}
            title="Swap Rows and Columns" aria-label="Swap Rows and Columns"><ArrowLeftRight size={17} /></button>
          {canEdit && <UndoRedoButtons history={history} />}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 220px', justifyContent: 'flex-end' }}>
          <button className="vdo-ghost" onClick={() => setPreviewOpen(true)} title="Open the visual as a viewer sees it">
            <Eye size={16} /> View as viewer
          </button>
          {canEdit ? (
            <button className="vdo-save" onClick={handleSave} disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}>
              {saving ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} />}
              {saving ? 'Saving…' : 'Save'}
              {isDirty && !saving && <span className="dirty" aria-label="Unsaved changes" />}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button className="vdo-ib" onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'} aria-label={embedFullscreen ? 'Restore' : 'Full screen'}>
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      <div className="vdo-body" ref={bodyRef}>
        {/* ── Floating settings panel: Data · Layout · Style ── */}
        {panelOpen ? (
          <>
          <aside className="vdo-float vdo-panel" style={{ width: panelSize.width }} aria-label="Visual settings">
            <div className="vdo-ptop">
              <div className="vdo-ptabs">
                {([
                  { key: 'data', label: 'Data', n: criteriaLeaves(model.SQLMODEL.CRITERIA.query.rules).length },
                  { key: 'layout', label: 'Layout', n: allCols.length },
                  { key: 'style', label: 'Style', n: 0 },
                ] as const).map((t) => (
                  <button key={t.key} className="vdo-pt" aria-pressed={tab === t.key} onClick={() => setTab(t.key)}>
                    {t.label}{t.n > 0 && <span className="n">{t.n}</span>}
                  </button>
                ))}
              </div>
              <button className="vdo-ib sm" onClick={() => setPanelOpen(false)} title="Collapse — more room for the chart" aria-label="Collapse the panel">
                <ChevronLeft size={15} />
              </button>
            </div>

            {tab === 'data' && (
              <div className="vdo-pbody vdo-cq">
                <DataSourceTab
                  model={model}
                  project={projectName}
                  schemaData={schemaData}
                  schemaList={schemaList}
                  schemaBusy={schemaBusy}
                  areaList={areaList}
                  areaSchemaName={areaSchemaName}
                  bank={baseBank}
                  projectVars={projectVars}
                  sqlHasStar={sqlHasStar}
                  sqlColsStatus={sqlColsStatus}
                  inlineBuilder={inlineQb}
                  builderHidden={qbHidden}
                  onShowBuilder={() => setQbHiddenPref(false)}
                  onModeChange={handleModeChange}
                  onSourceSqlChange={handleSourceSqlChange}
                  onSchemaNameChange={handleSchemaNameChange}
                  onBusinessAreaChange={(name) => void handleBusinessAreaChange(name)}
                  onEditSchema={() => setEditSchemaOpen(true)}
                  onAddSchemaCriteria={(key, groupPath) => addSchemaCriteria(key, baseBank, groupPath)}
                  onCriteriaChange={handleCriteriaChange}
                  onSqlBinding={handleSqlBinding}
                  onModelChange={updateModel}
                  dragItem={dragItem}
                  setDragItem={setDragItem}
                  fnCatalog={fnCatalog}
                />
                {/* The rows and the SQL behind the live preview. */}
                <div className="vdo-acc">
                  <button className="vdo-acc-h" aria-expanded={rowsOpen} onClick={() => setRowsOpen((o) => !o)}>
                    {rowsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    <span className="vdo-acc-t">Data</span>
                    <span className="vdo-acc-s">{rowsTable ? `${rowsTable.total} rows` : 'runs with the preview'}</span>
                  </button>
                  {rowsOpen && (
                    <div className="vdo-acc-b">
                      {rowsTable && rowsTable.cols.length ? (
                        <div className="vdo-rows">
                          <table>
                            <thead><tr>{rowsTable.cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                            <tbody>
                              {rowsTable.rows.map((r, i) => (
                                <tr key={i}>{rowsTable.cols.map((c) => <td key={c}>{r[c] == null ? '' : String(r[c])}</td>)}</tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                          Place fields on the visual — the rows behind the preview appear here (first 60).
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="vdo-acc">
                  <button className="vdo-acc-h" aria-expanded={queryOpen} onClick={() => setQueryOpen((o) => !o)}>
                    {queryOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    <span className="vdo-acc-t">Query</span>
                    <span className="vdo-acc-s">{mode === 'sql' ? 'your SQL, as run' : 'generated, read only'}</span>
                  </button>
                  {queryOpen && (
                    <div className="vdo-acc-b">
                      {payload?.sourceSql
                        ? <pre className="vdo-sql">{payload.sourceSql}</pre>
                        : <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>The query appears once the preview has run.</div>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'layout' && (
              <div className="vdo-pbody split">
                <div style={{
                  flex: wellMode === 'hidden' ? '1 1 auto' : `0 0 ${fieldsSize.width}px`, minWidth: 0, overflowY: 'auto',
                  padding: '4px 8px 16px 12px',
                }}>
                  <FieldTree
                    bank={bank}
                    mode={mode}
                    placedIn={placedIn}
                    readOnly={!canEdit}
                    emptyHint={sourceEmptyHint}
                    headerExtra={wellMode === 'hidden' ? (
                      <button className="vdo-showz" onClick={() => setWellMode('full')} title="Show the drop zones">
                        <LayoutGrid size={13} /> Drop zones <span className="c">{allCols.length}</span>
                      </button>
                    ) : null}
                    onToggle={(key, add) => (add ? addField(key) : removeField(key))}
                    onNewCalc={() => openCalc(null)}
                    onEditCalc={openCalc}
                    calcUnavailable={mode === 'sqlmodel'
                      ? (!schemaData ? 'Pick a schema or business area in the Data tab first' : '')
                      : 'Calculations work on a schema or business area. In SQL mode, write the formula in your SELECT.'}
                    onDragStart={(key) => setDragItem({ kind: 'bank', key })}
                    onDragEnd={() => setDragItem(null)}
                    canDropBack={dragItem?.kind === 'col'}
                    onDropBack={() => {
                      if (dragItem?.kind === 'col') moveColumn(dragItem.colId, null)
                      setDragItem(null)
                    }}
                  />
                </div>
                {wellMode !== 'hidden' && (
                  <div className={`vdo-split-r${fieldsSize.dragging ? ' on' : ''}`} {...fieldsSize.handle} />
                )}
                {wellMode !== 'hidden' && (
                  <div style={{ flex: '1 1 auto', minWidth: 0, overflowY: 'auto', display: 'flex' }}>
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                      <DropZones
                        groups={groups}
                        iconOf={iconOf}
                        mode={wellMode}
                        typeName={catalogEntry.VisualName}
                        readOnly={!canEdit}
                        dragging={!!dragItem}
                        canDrop={canDrop}
                        selectedColId={selection.kind === 'column' ? selection.colId : null}
                        colLabel={colLabel}
                        colGlyph={colGlyph}
                        colAgg={colAgg}
                        onDrop={dropOnZone}
                        onSelect={(colId) => setSelection(colId == null ? { kind: 'none' } : { kind: 'column', colId })}
                        onRemove={(colId) => moveColumn(colId, null)}
                        onDragStartCol={(colId) => setDragItem({ kind: 'col', colId })}
                        onDragEnd={() => setDragItem(null)}
                        renderEditor={(colId) => (
                          <ColumnProperties
                            model={model}
                            mode={mode}
                            selection={{ kind: 'column', colId }}
                            catalogEntry={catalogEntry}
                            findGroup={findGroupForColumn}
                            onColumnFieldChange={handleColumnFieldChange}
                            onMoveColumn={moveColumn}
                            fnCatalog={fnCatalog}
                            calcProblem={(key) => bankByKey.get(`${CALC_KEY_PREFIX}${key}`)?.calcProblem || ''}
                            onEditCalc={openCalc}
                          />
                        )}
                        setMode={setWellMode}
                        footer={layoutFooter}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'style' && (
              <div className="vdo-pbody" style={{ padding: '6px 16px 40px' }}>
                <StyleOptions
                  visualName={catalogEntry.VisualName}
                  options={model.LAYOUT_DATA.visualLayout.options || []}
                  defaults={optionDefaults}
                  columns={optionColumns}
                  readOnly={!canEdit}
                  onChange={handleOptionChange}
                  notice={mapNotice}
                  heading={{
                    title: model.LAYOUT_DATA.visualLayout.title || '',
                    subtitle: model.LAYOUT_DATA.visualLayout.subtitle || '',
                    titleFallback: objectName || 'the menu entry’s name',
                    onChange: (field, value) => updateModel((m) => {
                      const vl = m.LAYOUT_DATA.visualLayout
                      if (value) vl[field] = value
                      else delete vl[field]
                    }),
                  }}
                />
              </div>
            )}
          </aside>
          <div className={`vdo-resizer${panelSize.dragging ? ' on' : ''}`} {...panelSize.handle} />
          </>
        ) : (
          <div className="vdo-float vdo-rail">
            {([
              { key: 'data', label: 'Data', Icon: Database },
              { key: 'layout', label: 'Layout', Icon: LayoutGrid },
              { key: 'style', label: 'Style', Icon: Palette },
            ] as const).map(({ key, label, Icon }) => (
              <button key={key} onClick={() => { setTab(key); setPanelOpen(true) }} title={`Open ${label}`}>
                <Icon size={18} />{label}
              </button>
            ))}
          </div>
        )}

        {/* ── Canvas: the live preview with field chips on its axes ── */}
        <section className="vdo-canvas" aria-label="Live preview" aria-busy={previewBusy && previewReady}>
          {/* ── SQL mode, Data tab: build the query visually or type it,
              right above the chart it feeds. Every change is the visual's
              SOURCE_SQL at once (undo / save as usual); where the tables sit
              is kept per visual by the builder. ── */}
          {inlineQb && (qbHidden ? (
            <button type="button" className="vdo-float vdo-qbbar" onClick={() => setQbHiddenPref(false)}
              aria-expanded={false} data-tw-action="vdo-show-query-builder">
              <ChevronDown size={15} /> <b>Query</b>
              <span>build it visually or type the SQL</span>
            </button>
          ) : (
            <>
              <div className="vdo-float vdo-qbpane" style={{ height: qbHeight }} aria-label="Query builder">
                <Suspense fallback={<div className="qb-help" style={{ padding: 16 }}>Opening the query builder…</div>}>
                  <QueryBuilderWorkspace embedded targetLocked project={projectName}
                    sql={model.SOURCE_SQL || ''} onSqlChange={handleSourceSqlChange}
                    target={connectionId ? `conn:${connectionId}` : 'project'}
                    layoutKey={`visual:${objectName || 'new'}:SOURCE_SQL`}
                    headerStart={<span style={{ fontWeight: 700 }}>Query</span>}
                    headerEnd={(
                      <button type="button" className="qb-icon-btn" aria-label="Fold the query builder away"
                        title="Fold away — more room for the chart" onClick={() => setQbHiddenPref(true)}>
                        <ChevronUp size={16} />
                      </button>
                    )} />
                </Suspense>
              </div>
              <div className="vdo-qbsplit" role="separator" aria-orientation="horizontal"
                aria-label="Drag to resize the query builder" onPointerDown={startQbResize} />
            </>
          ))}
          <div className={`vdo-float vdo-card${previewBusy && previewReady ? ' vdo-busy' : ''}`}>
            {previewReady && (
              <>
                <div className="vdo-progress" aria-hidden="true" />
                <div className="vdo-busy-tag" role="status" aria-live="polite" aria-hidden={!previewBusy}>
                  <span className="sp" />Drawing {model.SQLMODEL.VISUAL_NAME}…
                </div>
              </>
            )}
            {autoNote && auto && (
              <div className="vdo-autonote" role="status" style={{ marginBottom: 10 }}>
                <span className="sp"><Sparkles size={14} /></span>
                <span style={{ flex: '1 1 220px' }}>Auto picked <b>{autoNote.visual}</b> — {autoNote.reason}.</span>
                {quick.filter((s) => s.visual !== model.SQLMODEL.VISUAL_NAME).slice(0, 2).map((s) => (
                  <button key={s.visual} className="vdo-tchip" onClick={() => pickVisual(s.visual)}>{s.visual}</button>
                ))}
                <button className="vdo-tchip w" onClick={() => { setAuto(false); setAutoNote(null) }} title="Turn Auto off and keep this visual">Keep</button>
                <button className="vdo-tchip" aria-label="Dismiss" onClick={() => setAutoNote(null)}><X size={13} /></button>
              </div>
            )}
            <div
              className={`vdo-drop${dragItem ? (canvasHot ? ' hot' : ' dropping') : ''}`}
              onDragOver={(e) => { if (draggingNewField && canEdit) { e.preventDefault(); if (!canvasHot) setCanvasHot(true) } }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setCanvasHot(false) }}
              onDrop={(e) => {
                e.preventDefault(); setCanvasHot(false)
                if (dragItem?.kind === 'bank') addField(dragItem.key)
                setDragItem(null)
              }}
            >
              <AxisShelves part="top" {...shelfProps} />
              {allCols.length === 0 ? (
                <div className="vdo-empty">
                  <ChartColumn size={40} />
                  <div style={{ fontWeight: 650, color: 'var(--color-text)', fontSize: 15 }}>Start by adding a field</div>
                  <div>{auto
                    ? 'Tick fields in Layout or drop them here — Auto chooses the visual as you go.'
                    : 'Tick fields in Layout, or drag them onto a drop zone or here.'}</div>
                  <div className="vdo-steps"><span><b>1</b>Pick the data</span><span><b>2</b>Tick or drag fields</span><span><b>3</b>See it live</span></div>
                </div>
              ) : (
                <div className={`vdo-previewwrap${previewEntering ? ' enter' : ''}`}
                  style={{ flex: '1 1 auto', minHeight: 300, display: 'flex', flexDirection: 'column' }}>
                  {previewReady && <VisualInlinePreview
                    project={projectName}
                    objName={objectName}
                    model={previewModel}
                    title={objectName}
                    onPayload={setPayload}
                    onBusy={setPreviewFetching}
                    onDatePick={canEdit && groups.some((g) => isEventsZone(g.StandardName))
                      ? (info) => { setTab('layout'); setEventDatePick(info) }
                      : undefined}
                  />}
                </div>
              )}
              <AxisShelves part="bottom" {...shelfProps} />
              {draggingNewField && (
                <div className="vdo-drop-hint">
                  {auto ? 'Drop anywhere — Auto places it and picks the visual' : 'Drop on an axis, or anywhere to place it'}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* In-place schema editor overlay */}
      {editSchemaOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--color-bg)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 12px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <div className="flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
              <Database size={15} style={{ color: 'var(--color-accent)' }} />
              Editing schema · {schemaName || '(unnamed)'}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setEditSchemaOpen(false); setSchemaNonce((n) => n + 1) }}
              title="Close the schema editor and return to the visual"
            >
              <X size={14} /> Done
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <SchemaDesignerPage
              embedded
              embedProject={projectName}
              embedObjName={schemaName}
            />
          </div>
        </div>
      )}

      {/* In-place document designer overlay.
          Same arrangement as the schema editor above, and for the same
          reason: the author is in the middle of one piece of work, and
          navigating away from a half-built visual to lay out its document
          loses the thread — and the unsaved model with it. */}
      {editDocumentOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--color-bg)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 12px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <div className="flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
              <FileText size={15} style={{ color: 'var(--color-accent)' }} />
              Designing the document · {objectName}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setEditDocumentOpen(false)
                // Re-read the pairing: the identity and the title may have
                // changed inside, and the panel is what reports them.
                setDocumentNonce((n) => n + 1)
              }}
              title="Close the document designer and return to the visual"
            >
              <X size={14} /> Done
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <DocumentFormDesignerPage
              embedded
              embedProject={projectName}
              embedObjName={objectName}
              /* Straight to the page. The source, the identity and the keys
                 were just written by the panel that opened this, so Settings
                 is a tab of answers the author did not ask a question for. */
              initialTab="body"
            />
          </div>
        </div>
      )}

      {calcDraft && (
        <CalculationDialog
          key={calcDraft.key || 'new'}
          draft={calcDraft}
          bank={baseBank}
          calcs={calcs}
          readOnly={!canEdit}
          onSave={saveCalc}
          onDelete={deleteCalc}
          onClose={() => setCalcDraft(null)}
        />
      )}

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />

      {/* Live preview — the shared standalone modal (a fixed overlay, outside
          this designer's layout) so the same surface serves the card "View"
          and app navigation without a duplicate implementation. */}
      <VisualPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        projectName={projectName}
        objectName={objectName}
        model={model}
      />
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
// Data Source tab
// ════════════════════════════════════════════════════════════════
function DataSourceTab({
  model, project, schemaData, schemaList, schemaBusy, areaList, areaSchemaName,
  bank, projectVars, sqlHasStar, sqlColsStatus, inlineBuilder, builderHidden, onShowBuilder,
  onModeChange, onSourceSqlChange, onSchemaNameChange, onBusinessAreaChange,
  onEditSchema,
  onAddSchemaCriteria, onCriteriaChange, onSqlBinding, onModelChange,
  dragItem, setDragItem, fnCatalog,
}: {
  model: VisualModel
  fnCatalog: VisualFunction[]
  project: string
  schemaData: VisualSchema | null
  schemaList: VisualSchemaSummary[]
  schemaBusy: boolean
  areaList: BusinessAreaSummary[]
  areaSchemaName: string
  bank: BankColumn[]
  projectVars: ProjectVariable[]
  /** The SELECT list uses `*` / `t.*` — columns come from the server. */
  sqlHasStar: boolean
  sqlColsStatus: { busy: boolean; error: string }
  /** The query builder is open inline above the chart (instead of the text box here). */
  inlineBuilder?: boolean
  builderHidden?: boolean
  onShowBuilder?: () => void
  onModeChange: (m: 'sql' | 'sqlmodel') => void
  onSourceSqlChange: (v: string) => void
  onSchemaNameChange: (name: string) => void
  onBusinessAreaChange: (name: string) => void
  onEditSchema: () => void
  onAddSchemaCriteria: (key: string, groupPath?: number[] | null) => void
  onCriteriaChange: (idx: number, field: keyof CriteriaRule, value: unknown) => void
  onSqlBinding: (varName: string, type: 'string' | 'number' | 'date' | undefined, defaultValue: string | undefined) => void
  onModelChange: (mutator: (m: VisualModel) => void) => void
  dragItem: DragItem | null
  setDragItem: (d: DragItem | null) => void
}) {
  const mode = model.DATA_SOURCE_MODE || 'sql'
  const businessArea = (model.SQLMODEL.BUSINESS_AREA || '').trim()
  const nodes = model.SQLMODEL.CRITERIA.query.rules
  // SQL mode's criteria are always FLAT (groups are a schema-mode feature).
  const rules = useMemo(() => criteriaLeaves(nodes), [nodes])
  const { systemTokens } = useMemo(
    () => deriveSqlCriteria(model.SOURCE_SQL || '', nodes),
    [model.SOURCE_SQL, nodes]
  )
  // SQL mode: a criteria card for a JUST-typed ?.placeholder mounts expanded;
  // the rest start collapsed behind their summaries.
  const knownIdsRef = useRef<Set<string> | null>(null)
  const knownIds = knownIdsRef.current
  const freshIds = knownIds
    ? new Set(rules.filter((r) => !knownIds.has(r.id)).map((r) => r.id))
    : new Set<string>()
  useEffect(() => {
    knownIdsRef.current = new Set(rules.map((r) => r.id))
  }, [rules])
  // Expand/collapse-ALL broadcast for the SQL-mode criteria cards.
  const [cardSignal, setCardSignal] = useState<{ open: boolean; seq: number }>({ open: false, seq: 0 })

  // ── Collapsible top sections ──
  // Once a Business Area is picked, sections 1 & 2 minimize to one-line
  // headers (the picked name stays visible in the header); clicking a header
  // re-expands it. Opening the designer with an area already set starts
  // collapsed so the working area (columns + criteria) gets the space.
  const [baOpen, setBaOpen] = useState(!businessArea)
  const [dsOpen, setDsOpen] = useState(!businessArea)
  const prevAreaRef = useRef(businessArea)
  useEffect(() => {
    if (prevAreaRef.current === businessArea) return
    prevAreaRef.current = businessArea
    if (businessArea) { setBaOpen(false); setDsOpen(false) }
    else setBaOpen(true)
  }, [businessArea])

  const modeLabel = mode === 'sqlmodel'
    ? (businessArea
        ? 'Business Area dataset'
        : `Schema${(model.SQLMODEL.SCHEMA_NAME || '').trim() ? ` · ${model.SQLMODEL.SCHEMA_NAME}` : ''}`)
    : 'SQL Query'

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 28px 60px', background: 'var(--color-bg)' }}>
      <div style={{ maxWidth: 1040 }}>

      {/* 1 · Business area — the analytics basis of this visual. 'Default'
          needs no definition: the visual reads the project database / its own
          schema exactly as before. */}
      <SectionToggleHeader
        label="1 · Business area"
        badge={<span style={newBadgeStyle}>New</span>}
        open={baOpen}
        summary={businessArea || 'Default'}
        onToggle={() => setBaOpen((o) => !o)}
      />
      {baOpen && (
        <>
          <div style={helperStyle}>
            Pick the topic this visual belongs to — its tables, folders and security
            come along automatically.
          </div>
          <div style={{ margin: '12px 0 26px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <AreaCard
                name="Default"
                description="No business area — the visual reads the project database / its own schema directly."
                dotColor="var(--color-text-faint)"
                active={!businessArea}
                onPick={() => { if (businessArea) onBusinessAreaChange('') }}
              />
              {areaList.map((a, i) => (
                <AreaCard
                  key={a.area_name}
                  name={a.area_name}
                  description={a.description || 'Business area'}
                  tableCount={a.table_count}
                  dotColor={AREA_DOT_COLORS[i % AREA_DOT_COLORS.length]}
                  active={businessArea === a.area_name}
                  onPick={() => { if (businessArea !== a.area_name) onBusinessAreaChange(a.area_name) }}
                />
              ))}
            </div>
            {businessArea && (
              <div style={hintStyle}>
                Data comes from Business Area <strong>{businessArea}</strong>
                {areaSchemaName
                  ? <> (dataset: schema <strong>{areaSchemaName}</strong>)</>
                  : <> (its own tables &amp; joins)</>}
                {' '}— including its data-source connection and row/column security.
              </div>
            )}
          </div>
        </>
      )}

      {/* 2 · Data source — schema-vs-SQL choice cards */}
      <div style={{ marginTop: baOpen ? 0 : 10 }}>
        <SectionToggleHeader
          label="2 · Data source"
          open={dsOpen}
          summary={modeLabel}
          onToggle={() => setDsOpen((o) => !o)}
        />
      </div>
      {dsOpen && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, margin: '12px 0 26px', maxWidth: 720 }}>
          <ModeButton
            active={mode === 'sqlmodel'}
            label={businessArea ? 'Business Area dataset' : 'Schema'}
            hint={businessArea
              ? 'Use the business area’s model — drag its columns to build the layout & filters. Recommended.'
              : 'Use a reusable schema model — drag columns to build the layout & filters. Recommended.'}
            onClick={() => onModeChange('sqlmodel')}
          />
          <ModeButton
            active={mode === 'sql'}
            label="SQL Query"
            hint="Write SQL — columns come from the SELECT, prompts from ?. placeholders."
            onClick={() => onModeChange('sql')}
          />
        </div>
      )}
      {!dsOpen && <div style={{ height: 14 }} />}

      {mode === 'sql' ? (
        <div className="vdo-dgrid vdo-dgrid-sql">
          {/* SQL editor */}
          <div>
            {inlineBuilder ? (
              <>
                <SectionHeader>SQL query</SectionHeader>
                <div style={{ ...hintStyle, marginTop: 0, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 11, background: 'var(--color-bg-secondary)' }}>
                  {builderHidden ? (
                    <>The query builder is folded away.{' '}
                      <button type="button" className="vdo-linkbtn" onClick={onShowBuilder}>Show it</button>{' '}
                      to build the query visually or type the SQL.</>
                  ) : (
                    <>The query is open above the chart: <strong>Build visually</strong> to draw it —
                      drag tables, turn on <strong>Join mode</strong> and click two columns to join them — or
                      <strong> Type SQL</strong>. Every change redraws the chart.</>
                  )}
                </div>
              </>
            ) : (
              <>
            <SectionHeader actions={(
              <SqlBuilderButton
                value={model.SOURCE_SQL || ''}
                onChange={onSourceSqlChange}
                project={project}
                target={model.SQLMODEL?.DATABASE_DETAIL?.CONNECTION_ID
                  ? `conn:${model.SQLMODEL.DATABASE_DETAIL.CONNECTION_ID}` : undefined}
                title="Visual source SQL"
              />
            )}>SQL query</SectionHeader>
            <textarea
              value={model.SOURCE_SQL || ''}
              onChange={(e) => onSourceSqlChange(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 320, fontFamily: MONO_FONT, fontSize: 14.5,
                padding: '12px 14px', background: 'var(--color-input-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 11,
                resize: 'vertical', lineHeight: 1.6,
              }}
              placeholder="SELECT t.region AS region, SUM(t.value) AS total&#10;FROM sales t&#10;WHERE t.txn_date BETWEEN ?.from_date AND ?.to_date&#10;  AND t.site = ?.@LoginSite&#10;GROUP BY t.region"
            />
              </>
            )}
            <div style={hintStyle}>
              Columns come from the <code>SELECT</code> aliases (place them on the
              chart in the <strong>Layout</strong> tab). Use <code>?.name</code> for a
              user prompt and <code>?.@Token</code> for system inputs (LoginEmployee,
              Today, 1stDayOfMonth…).
            </div>
            {/* `SELECT *` names no columns — the database is asked what the
                query really returns, so the bank shows real columns, not `*`. */}
            {sqlHasStar && (
              <div style={{
                ...hintStyle,
                color: sqlColsStatus.error ? 'var(--color-error)' : undefined,
              }}>
                {sqlColsStatus.busy
                  ? 'Reading the query’s columns from the database…'
                  : sqlColsStatus.error
                  ? `Could not expand ${'*'} — ${sqlColsStatus.error}`
                  : bank.length
                  ? `${'*'} expanded to ${bank.length} column${bank.length === 1 ? '' : 's'} from the database.`
                  : `${'*'} will be expanded from the database once the query runs.`}
              </div>
            )}
          </div>

          {/* Auto-derived criteria */}
          <div>
            <SectionHeader
              actions={rules.length > 0 && (
                <FolderExpandControls
                  expandTitle="Expand all criteria"
                  collapseTitle="Collapse all criteria"
                  onExpandAll={() => setCardSignal((s) => ({ open: true, seq: s.seq + 1 }))}
                  onCollapseAll={() => setCardSignal((s) => ({ open: false, seq: s.seq + 1 }))}
                />
              )}
            >
              Criteria ({rules.length})
              <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginLeft: 6 }}>
                · auto from <code>?.</code> prompts
              </span>
            </SectionHeader>
            {rules.length === 0 && (
              <div style={emptyHintStyle}>
                Add a <code>?.placeholder</code> to the SQL — a criterion appears here
                automatically.
              </div>
            )}
            {rules.map((rule, i) => (
              <CriteriaCard
                key={rule.id || i}
                rule={rule}
                idx={i}
                project={project}
                allRules={rules}
                fieldLocked
                sqlMode
                projectVars={projectVars}
                defaultOpen={freshIds.has(rule.id)}
                expandSignal={cardSignal}
                onChange={onCriteriaChange}
                onSqlBinding={onSqlBinding}
              />
            ))}
            {systemTokens.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ ...hintStyle, marginBottom: 4 }}>System inputs (resolved automatically):</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {systemTokens.map((t) => (
                    <span key={t} style={tokenChipStyle}>?.@{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <SchemaSource
          model={model}
          project={project}
          schemaData={schemaData}
          schemaList={schemaList}
          schemaBusy={schemaBusy}
          businessArea={businessArea}
          areaSchemaName={areaSchemaName}
          bank={bank}
          nodes={nodes}
          projectVars={projectVars}
          onSchemaNameChange={onSchemaNameChange}
          onEditSchema={onEditSchema}
          onAddSchemaCriteria={onAddSchemaCriteria}
          onModelChange={onModelChange}
          dragItem={dragItem}
          setDragItem={setDragItem}
          fnCatalog={fnCatalog}
        />
      )}
      </div>
    </div>
  )
}

// ── Schema-mode data source: picker + tree + criteria builder ─────
function SchemaSource({
  model, project, schemaData, schemaList, schemaBusy, businessArea,
  areaSchemaName, bank, nodes, projectVars,
  onSchemaNameChange, onEditSchema, onAddSchemaCriteria,
  onModelChange, dragItem, setDragItem, fnCatalog,
}: {
  model: VisualModel
  fnCatalog: VisualFunction[]
  project: string
  schemaData: VisualSchema | null
  schemaList: VisualSchemaSummary[]
  schemaBusy: boolean
  businessArea: string
  areaSchemaName: string
  bank: BankColumn[]
  nodes: CriteriaNode[]
  projectVars: ProjectVariable[]
  onSchemaNameChange: (name: string) => void
  onEditSchema: () => void
  onAddSchemaCriteria: (key: string, groupPath?: number[] | null) => void
  onModelChange: (mutator: (m: VisualModel) => void) => void
  dragItem: DragItem | null
  setDragItem: (d: DragItem | null) => void
}) {
  const schemaName = model.SQLMODEL.SCHEMA_NAME || ''
  const tables = useMemo(() => groupByTable(bank), [bank])
  const [dropActive, setDropActive] = useState(false)
  // Which condition GROUP the current drag is hovering (its own drop zone),
  // as the group's index-path key, e.g. '2' or '2.0' for a nested group.
  const [groupDrop, setGroupDrop] = useState<string | null>(null)
  const leaves = useMemo(() => criteriaLeaves(nodes), [nodes])
  // Freshly-dropped criteria mount EXPANDED; existing ones start collapsed
  // (the collapsed header keeps a one-line summary visible).
  const knownIdsRef = useRef<Set<string> | null>(null)
  const knownIds = knownIdsRef.current
  const freshIds = knownIds
    ? new Set(leaves.filter((r) => !knownIds.has(r.id)).map((r) => r.id))
    : new Set<string>()
  useEffect(() => {
    knownIdsRef.current = new Set(leaves.map((r) => r.id))
  }, [leaves])

  // ── Criteria mutations — addressed by GROUP PATH (index path from the
  // top-level rules to a group, [] = top level) + an index inside that
  // group's list. Groups nest to any depth. ──
  const listAt = (m: VisualModel, groupPath: number[]): CriteriaNode[] | null => {
    const rules = m.SQLMODEL.CRITERIA.query.rules
    if (!groupPath.length) return rules
    const group = criteriaGroupAtPath(rules, groupPath)
    return group ? group.rules : null
  }
  const changeAt = (groupPath: number[], idx: number) =>
    (_i: number, field: keyof CriteriaRule, value: unknown) =>
      onModelChange((m) => {
        const node = listAt(m, groupPath)?.[idx]
        if (node && !isCriteriaGroup(node)) (node as Record<string, unknown>)[field as string] = value
      })
  const deleteAt = (groupPath: number[], idx: number) => () =>
    onModelChange((m) => {
      listAt(m, groupPath)?.splice(idx, 1)
    })
  // Add a condition group — at the top level or nested inside an existing
  // group. New groups default to OR (the typical use is `(a OR b)`
  // alternatives AND-ed with the rest) and get a unique designer-only id
  // (collapse-state key; engines ignore it).
  const addGroupAt = (groupPath: number[]) =>
    onModelChange((m) => {
      const list = listAt(m, groupPath)
      if (!list) return
      const ids = new Set<string>()
      const collect = (ns: CriteriaNode[]) => ns.forEach((n) => {
        if (isCriteriaGroup(n)) { if (typeof n.id === 'string') ids.add(n.id); collect(n.rules || []) }
      })
      collect(m.SQLMODEL.CRITERIA.query.rules)
      let g = 1
      while (ids.has(`Group_${g}`)) g += 1
      list.push({ condition: 'or', rules: [], id: `Group_${g}` })
    })
  const setGroupCondition = (groupPath: number[], cond: 'and' | 'or') =>
    onModelChange((m) => {
      const group = criteriaGroupAtPath(m.SQLMODEL.CRITERIA.query.rules, groupPath)
      if (group) group.condition = cond
    })
  const ungroupAt = (groupPath: number[]) =>
    onModelChange((m) => {
      const parentList = listAt(m, groupPath.slice(0, -1))
      const idx = groupPath[groupPath.length - 1]
      const node = parentList?.[idx]
      if (parentList && node && isCriteriaGroup(node)) parentList.splice(idx, 1, ...node.rules)
    })
  // ── Group collapse state (keyed by the group's designer id; default open)
  // + the expand/collapse-ALL signal the criteria cards listen to. ──
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({})
  const [cardSignal, setCardSignal] = useState<{ open: boolean; seq: number }>({ open: false, seq: 0 })
  const groupKey = (g: CriteriaGroup, path: number[]) => g.id || `path:${path.join('.')}`
  const isGroupOpen = (g: CriteriaGroup, path: number[]) => groupOpen[groupKey(g, path)] ?? true
  const setAllCriteriaOpen = (open: boolean) => {
    const next: Record<string, boolean> = {}
    const walk = (ns: CriteriaNode[], path: number[]) => ns.forEach((n, i) => {
      if (isCriteriaGroup(n)) { next[groupKey(n, [...path, i])] = open; walk(n.rules || [], [...path, i]) }
    })
    walk(nodes, [])
    setGroupOpen(next)
    setCardSignal((s) => ({ open, seq: s.seq + 1 }))
  }
  // Folder open state — by default every folder is COLLAPSED except the
  // first, so long schemas open compact. Header buttons expand/collapse all.
  const [folderOverrides, setFolderOverrides] = useState<Record<string, boolean>>({})
  const isFolderOpen = (name: string, idx: number) => folderOverrides[name] ?? idx === 0
  const setAllFolders = (open: boolean) => {
    const next: Record<string, boolean> = {}
    for (const t of tables) next[t.table] = open
    setFolderOverrides(next)
  }
  // Enforced per-table filters defined on the schema — surfaced here so the
  // author knows the query will carry them automatically (§8.3.3).
  const condTables = useMemo(
    () => (schemaData?.TABLES || []).filter((t) => (t.CONDITION || '').trim()),
    [schemaData]
  )

  // ── Recursive criteria renderers — a list of nodes is a responsive card
  // grid; a GROUP is a full-width collapsible box (own drop zone + and/or)
  // whose rules render with the same grid, so groups nest to any depth. ──
  function renderGroupBox(group: CriteriaGroup, path: number[], depth: number) {
    const pathKey = path.join('.')
    const cond = (group.condition || 'or').toLowerCase() === 'and' ? 'and' : 'or'
    const open = isGroupOpen(group, path)
    const leafCount = criteriaLeaves(group.rules || []).length
    return (
      <div
        key={group.id || `group-${pathKey}`}
        onDragOver={(e) => {
          if (dragItem?.kind === 'bank') {
            e.preventDefault(); e.stopPropagation(); setGroupDrop(pathKey)
          }
        }}
        onDragLeave={() => setGroupDrop((g) => (g === pathKey ? null : g))}
        onDrop={(e) => {
          e.preventDefault(); e.stopPropagation()
          setGroupDrop(null); setDropActive(false)
          if (dragItem?.kind === 'bank') {
            onAddSchemaCriteria(dragItem.key, path)
            // The fresh card mounts expanded — make sure its group is open too.
            setGroupOpen((prev) => ({ ...prev, [groupKey(group, path)]: true }))
          }
          setDragItem(null)
        }}
        style={{
          gridColumn: '1 / -1',
          border: `1.5px ${groupDrop === pathKey ? 'dashed var(--color-accent)' : 'solid var(--color-border-strong)'}`,
          borderRadius: 10, padding: 10,
          background: groupDrop === pathKey
            ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)'
            : depth % 2 ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
        }}
      >
        {/* Header — click to collapse/expand; the controls stay clickable. */}
        <div
          onClick={() => setGroupOpen((prev) => ({ ...prev, [groupKey(group, path)]: !open }))}
          title={open ? 'Collapse this group — a one-line summary stays visible' : 'Expand this group'}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: open ? 8 : 0, flexWrap: 'wrap', cursor: 'pointer' }}
        >
          {open
            ? <ChevronDown size={13} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
            : <ChevronRight size={13} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />}
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Condition group
          </span>
          {open ? (
            <>
              <select
                value={cond}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setGroupCondition(path, e.target.value as 'and' | 'or')}
                title="How the conditions INSIDE this group combine"
                style={{ ...selectStyle, width: 'auto', padding: '3px 6px', fontSize: 13.5 }}
              >
                <option value="or">match ANY (OR)</option>
                <option value="and">match ALL (AND)</option>
              </select>
              <span style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>
                renders as ( … {cond.toUpperCase()} … )
              </span>
            </>
          ) : (
            <span style={{
              flex: 1, minWidth: 0, fontSize: 13, color: 'var(--color-text)',
              padding: '1px 8px', borderRadius: 6,
              background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {leafCount} filter{leafCount === 1 ? '' : 's'} · {cond.toUpperCase()}
            </span>
          )}
          {open && <span style={{ flex: 1 }} />}
          {open && (
            <button
              className="btn btn-ghost btn-sm"
              style={{ fontSize: 13 }}
              onClick={(e) => { e.stopPropagation(); addGroupAt(path) }}
              title="Add a nested condition group inside this one, e.g. (a OR (b AND c))"
            >
              <Plus size={12} /> Sub-group
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            style={{ fontSize: 13, ...(open ? {} : { marginLeft: 'auto' }) }}
            onClick={(e) => { e.stopPropagation(); ungroupAt(path) }}
            title="Dissolve the group — its contents move up one level (nothing is deleted)"
          >
            Ungroup
          </button>
        </div>
        {open && (group.rules || []).length === 0 && (
          <div style={emptyHintStyle}>
            Drag a column here — conditions inside this box combine with {cond.toUpperCase()}.
          </div>
        )}
        {open && renderNodes(group.rules || [], path, depth + 1)}
      </div>
    )
  }
  function renderNodes(list: CriteriaNode[], groupPath: number[], depth: number): React.ReactNode {
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: 10,
      }}>
        {list.map((node, i) => {
          if (isCriteriaGroup(node)) return renderGroupBox(node, [...groupPath, i], depth)
          const rule = node
          if (rule.applyTo === 'row_limit') {
            // Managed by the Sort & Top N panel below — show a read-only chip
            // here so the rule is visible (it renders as a prompt at runtime)
            // without offering column/operator edits that don't apply to it.
            return (
              <div
                key={rule.id || `${groupPath.join('.')}-${i}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 10px', borderRadius: 9, fontSize: 13.5,
                  border: '1px dashed var(--color-border-strong)',
                  background: 'var(--color-surface-2)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {rule.promptLabel || 'Top N'} prompt
                </span>
                <span style={{ color: 'var(--color-text-faint)' }}>
                  row limit · default {rule.value || '—'} · edit in “Sort &amp; Top N” below
                </span>
                <span style={{ flex: 1 }} />
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={deleteAt(groupPath, i)}
                  title="Remove the Top N prompt (no row limit)"
                >
                  <X size={13} />
                </button>
              </div>
            )
          }
          if (rule.applyTo === 'having' || (rule.FUNCTION || '').trim()) {
            // Function condition (HAVING): dialect-supported function over a
            // dropped column or a free-hand expression.
            return (
              <FunctionCriteriaCard
                key={rule.id || `${groupPath.join('.')}-${i}`}
                rule={rule}
                functions={fnCatalog.filter((f) => f.kind !== 'window')}
                bank={bank}
                dragItem={dragItem}
                onChange={(field, value) => changeAt(groupPath, i)(0, field, value)}
                onDelete={deleteAt(groupPath, i)}
              />
            )
          }
          return (
            <CriteriaCard
              key={rule.id || `${groupPath.join('.')}-${i}`}
              rule={rule}
              idx={leaves.indexOf(rule)}
              project={project}
              allRules={leaves}
              projectVars={projectVars}
              defaultOpen={freshIds.has(rule.id)}
              expandSignal={cardSignal}
              onChange={changeAt(groupPath, i)}
              onDelete={deleteAt(groupPath, i)}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div>
      {businessArea ? (
        /* Dataset fixed by the Business Area — no schema picking needed. */
        <div style={hintStyle}>
          Dataset from Business Area <strong>{businessArea}</strong>
          {areaSchemaName && <> via schema <strong>{areaSchemaName}</strong></>}
          . Change it in the Business Area designer, or switch &quot;Based
          on&quot; back to Default to pick a schema directly.
        </div>
      ) : (
        <>
          {/* Schema picker row */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <div>
              <div style={fieldLabelStyle}>Schema</div>
              <input
                list="visual-schema-list"
                value={schemaName}
                onChange={(e) => onSchemaNameChange(e.target.value)}
                placeholder="Select or name a schema"
                style={{ ...inputStyle, width: 280 }}
              />
              <datalist id="visual-schema-list">
                {schemaList.map((s) => (
                  <option key={s.schema_name} value={s.schema_name}>
                    {s.schema_descr ? `${s.schema_descr} · ${s.table_count} tables` : `${s.table_count} tables`}
                  </option>
                ))}
              </datalist>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={onEditSchema}
              disabled={!schemaName}
              title={schemaName ? 'Open the schema designer' : 'Name a schema first'}
            >
              <Pencil size={13} /> Edit schema design
            </button>
          </div>
          <div style={hintStyle}>
            A schema is a reusable dataset (tables + joins). Multiple visuals can share
            one. Drag its columns into <strong>Criteria</strong> below to build filters, or
            onto the chart in the <strong>Layout</strong> tab.
          </div>
        </>
      )}

      {/* Filters injected automatically from the Schema Design (§8.3.3) */}
      {condTables.length > 0 && (
        <div style={{
          maxWidth: 720, marginTop: 16, padding: '12px 15px', borderRadius: 11,
          background: 'var(--color-warning-weak)', border: '1px solid var(--color-warning)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-warning)', marginBottom: 6 }}>
            Filters injected automatically from the Schema Design
          </div>
          {condTables.map((t) => (
            <div
              key={t.TABLE_NAME}
              style={{ fontFamily: MONO_FONT, fontSize: 14, color: 'var(--color-text-secondary)', padding: '1.5px 0' }}
            >
              {t.TABLE_NAME} · {t.CONDITION}
            </div>
          ))}
          <div style={{ fontSize: 14, color: 'var(--color-text-faint)', marginTop: 6, lineHeight: 1.5 }}>
            Whenever a column from these tables is used, its condition is added to the query for you.
          </div>
        </div>
      )}

      {/* Tree + criteria builder. Each pane owns its scroll (bounded height)
          so scrolling the column list never moves the criteria drop area —
          drag & drop works with both lists at any scroll position. */}
      <div className="vdo-dgrid vdo-dgrid-schema">
        {/* Schema column tree */}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...eyebrowStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4, marginBottom: 8 }}>
            <span>Schema columns</span>
            {tables.length > 1 && (
              <FolderExpandControls
                onExpandAll={() => setAllFolders(true)}
                onCollapseAll={() => setAllFolders(false)}
              />
            )}
          </div>
          <div style={{
            ...panelStyle, padding: 8, minHeight: 140,
            maxHeight: 'min(62vh, 560px)', overflowY: 'auto',
          }}>
            {schemaBusy && <div style={emptyHintStyle}><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Loading schema…</div>}
            {!schemaBusy && !schemaName && (
              <div style={emptyHintStyle}>Pick or name a schema above to see its columns.</div>
            )}
            {!schemaBusy && schemaName && !schemaData && (
              <div style={emptyHintStyle}>
                Schema <strong>{schemaName}</strong> has no tables yet.
                Click <em>Edit schema design</em> to define tables & columns.
              </div>
            )}
            {!schemaBusy && schemaData && tables.length === 0 && (
              <div style={emptyHintStyle}>No columns defined in this schema.</div>
            )}
            {tables.map((t, i) => (
              <SchemaTreeTable
                key={t.table}
                table={t.table}
                columns={t.columns}
                open={isFolderOpen(t.table, i)}
                onToggle={() => setFolderOverrides((prev) => ({
                  ...prev, [t.table]: !isFolderOpen(t.table, i),
                }))}
                setDragItem={setDragItem}
              />
            ))}
          </div>
        </div>

        {/* Criteria drop-zone — own scroll; cards flow in a responsive
            multi-column grid (mirrors the live-preview criteria bar).
            Condition GROUPS render as full-width collapsible boxes with their
            own drop zone + and/or, and nest to any depth, so e.g.
            `(site = x OR (region = y AND tier = 1)) AND date > ?`
            is authored visually. */}
        <div style={{ minWidth: 0 }}>
          <div style={{ ...eyebrowStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4, marginBottom: 8 }}>
            <span className="flex items-center gap-1"><Filter size={12} /> Criteria ({leaves.length})</span>
            {(leaves.length > 0 || nodes.length > 0) && (
              <FolderExpandControls
                expandTitle="Expand all criteria"
                collapseTitle="Collapse all criteria"
                onExpandAll={() => setAllCriteriaOpen(true)}
                onCollapseAll={() => setAllCriteriaOpen(false)}
              />
            )}
          </div>
          <div
            onDragOver={(e) => { if (dragItem?.kind === 'bank') { e.preventDefault(); setDropActive(true) } }}
            onDragLeave={() => setDropActive(false)}
            onDrop={(e) => {
              e.preventDefault(); setDropActive(false)
              if (dragItem?.kind === 'bank') onAddSchemaCriteria(dragItem.key)
              setDragItem(null)
            }}
            style={{
              border: `1.5px dashed ${dropActive && groupDrop == null ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: 11, padding: 10, minHeight: 140,
              maxHeight: 'min(62vh, 560px)', overflowY: 'auto',
              background: dropActive && groupDrop == null
                ? 'color-mix(in srgb, var(--color-accent) 10%, transparent)'
                : 'var(--color-bg-secondary)',
            }}
          >
            {nodes.length === 0 && (
              <div style={emptyHintStyle}>Drag a column here to add a filter — compare it with a user prompt, a fixed value, or a system variable. Drop the same column twice to build a range (e.g. from/to dates).</div>
            )}
            {renderNodes(nodes, [], 0)}
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <Field label="Combine filters & groups with">
              <select
                value={model.SQLMODEL.CRITERIA.query.condition || 'and'}
                onChange={(e) => onModelChange((m) => { m.SQLMODEL.CRITERIA.query.condition = e.target.value as 'and' | 'or' })}
                style={{ ...selectStyle, width: 120 }}
              >
                <option value="and">AND</option>
                <option value="or">OR</option>
              </select>
            </Field>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => addGroupAt([])}
              title="Add a condition group — filters dropped inside combine with their own AND/OR, e.g. (site = x OR site IS NULL). Groups can hold sub-groups for deeper nesting."
              style={{ marginBottom: 10 }}
            >
              <Plus size={13} /> Add condition group
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => onModelChange((m) => {
                const list = m.SQLMODEL.CRITERIA.query.rules
                const used = criteriaLeaves(list)
                let n = 1
                while (used.some((r) => r.id === `Criteria_${n}`)) n += 1
                list.push({
                  id: `Criteria_${n}`, field: '', COLTYPE: 'number',
                  currentFieldType: 'number', operator: '>', queryOption: 'F',
                  prevOption: 'F', customValue: 'FixedValue', value: '',
                  chipValue: [], FUNCTION: 'SUM', applyTo: 'having',
                } as CriteriaRule)
              })}
              title="Add a function condition — a database function (SUM, COUNT DISTINCT, MEDIAN, …, as supported by the bound database) over a dropped column or a free-hand expression, applied AFTER aggregation (HAVING)."
              style={{ marginBottom: 10 }}
            >
              <Plus size={13} /> Add function condition
            </button>
          </div>
        </div>
      </div>

      {/* Sort & Top N — ORDER_BY / ROW_LIMIT / the Top-N prompt rule. */}
      <SortTopNPanel model={model} onModelChange={onModelChange} />

      <div style={{ ...hintStyle, marginTop: 18 }}>
        Next: open the <strong>Layout</strong> tab and drag columns onto the chart.
      </div>
    </div>
  )
}

function SchemaTreeTable({
  table, columns, open, onToggle, setDragItem,
}: {
  table: string
  columns: BankColumn[]
  open: boolean
  onToggle: () => void
  setDragItem: (d: DragItem | null) => void
}) {
  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, width: '100%', border: 'none',
          background: 'transparent', cursor: 'pointer', padding: '3px 2px',
          fontSize: 13, fontWeight: 600, color: 'var(--color-text)',
        }}
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Table2 size={12} style={{ opacity: 0.7 }} />
        {table}
        <span style={{ fontWeight: 400, fontSize: 13, color: 'var(--color-text-secondary)' }}>
          ({columns.length})
        </span>
      </button>
      {open && (
        <div style={{ paddingLeft: 16 }}>
          {columns.map((c) => (
            <div
              key={c.key}
              className="vdx-bankchip"
              draggable
              onDragStart={(e) => { e.dataTransfer.effectAllowed = 'copy'; e.dataTransfer.setData('text/plain', c.key); setDragItem({ kind: 'bank', key: c.key }) }}
              onDragEnd={() => setDragItem(null)}
              style={{ background: 'var(--color-bg)' }}
              title={`${c.DBTABLE}.${c.DBNAME} · ${c.type}`}
            >
              <GripVertical size={11} style={{ opacity: 0.5, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.label}
              </span>
              <TypeBadge type={c.type} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
// Layout tab
// ════════════════════════════════════════════════════════════════
function TypeBadge({ type }: { type: string }) {
  const isDate = (type || '').toLowerCase() === 'date'
  return (
    <span style={{
      fontSize: 13, fontWeight: 600, padding: '1px 7px', borderRadius: 5, flexShrink: 0,
      background: isDate ? 'var(--color-info-weak)' : 'var(--color-surface-2)',
      border: isDate
        ? '1px solid color-mix(in srgb, var(--color-info) 45%, transparent)'
        : '1px solid var(--color-border)',
      color: isDate ? 'var(--color-info)' : 'var(--color-text-faint)',
    }}>{type}</span>
  )
}
function AggBadge({ fn }: { fn: string }) {
  return (
    <span style={{
      fontSize: 13, fontWeight: 700, padding: '1px 7px', borderRadius: 5, flexShrink: 0,
      background: 'var(--color-success-weak)',
      border: '1px solid color-mix(in srgb, var(--color-success) 45%, transparent)',
      color: 'var(--color-success)',
    }} title={`Aggregated: ${fn}`}>{fn}</span>
  )
}

// ── Column properties (Layout right panel) ────────────────────────
function ColumnProperties({
  model, mode, selection, catalogEntry, findGroup, onColumnFieldChange, onMoveColumn,
  fnCatalog, calcProblem, onEditCalc,
}: {
  model: VisualModel
  fnCatalog: VisualFunction[]
  /** The problem stopping a calculation (by its key) from running ('' = none). */
  calcProblem?: (calcKey: string) => string
  onEditCalc?: (calcKey: string) => void
  mode: string
  selection: Selection
  catalogEntry: VisualCatalogEntry
  findGroup: (colId: number) => string | null
  onColumnFieldChange: (colId: number, field: keyof VisualColumn, value: unknown) => void
  onMoveColumn: (colId: number, std: string | null) => void
}) {
  const [formatOpen, setFormatOpen] = useState(false)
  if (selection.kind !== 'column') {
    return (
      <div style={{ ...emptyHintStyle, padding: '36px 14px', textAlign: 'center' }}>
        Select a placed column to edit how it appears.
      </div>
    )
  }
  let col: VisualColumn | undefined
  for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    const f = (g.COLUMNS || []).find((c) => c.COLID === selection.colId)
    if (f) { col = f; break }
  }
  if (!col) {
    return (
      <div style={{ ...emptyHintStyle, padding: '36px 14px', textAlign: 'center' }}>
        Select a placed column.
      </div>
    )
  }
  const isSchema = mode === 'sqlmodel'
  const currentGroup = findGroup(col.COLID)
  const c = col

  // ── Ranking-function context (window FUNCTION + its OVER_ORDER_BY) ────────
  const allModelColumns: VisualColumn[] = []
  for (const g of model.SQLMODEL.SQLModel?.COLUMNS || []) {
    for (const mc of g.COLUMN || []) if (mc?.NAME) allModelColumns.push(mc)
  }
  const isWindowFn = fnCatalog.some(
    (f) => f.name === (c.FUNCTION || '').toUpperCase() && f.kind === 'window',
  )
  const rawRankBy = Array.isArray(c.OVER_ORDER_BY)
    ? (c.OVER_ORDER_BY as { column?: string; direction?: string }[])[0]
    : undefined
  const rankBy = rawRankBy
    ? { column: String(rawRankBy.column || ''), direction: String(rawRankBy.direction || 'DESC') }
    : undefined

  // ── Column formatting context ──────────────────────────────────────────────
  const display = c.display as DisplayFormat | undefined
  const isCalc = c.CALC_TYPE === 'Presentation'
  const calcInfo = calcInfoOf(c)
  const calcDef = calcInfo ? calculationsOf(model).find((x) => x.key === calcInfo.key) : undefined
  const calcBad = calcInfo ? (calcProblem?.(calcInfo.key) || '') : ''
  const groupLc = (currentGroup || '').toLowerCase()
  const isPivot = catalogEntry.ID === 'pivot'
  const isValuesGroup = groupLc === 'values' || groupLc === 'measures'
  const showPivotAgg = isPivot && isValuesGroup && !isCalc
  const pivotAgg = String(c.PIVOT_AGG || '')
  const showDirection = showPivotAgg && POSITIONAL_PIVOT_AGGS.has(pivotAgg)

  // Adapter: row data is keyed by NAME, so db_name = NAME makes companion
  // pickers + the live preview inside DisplayFormatEditor resolve.
  const formatColumn: ColumnDef = {
    name: c.NAME,
    db_name: c.NAME,
    heading: c.content || c.NAME,
    col_id: String(c.COLID),
    group_name: '',
    col_type: c.COLTYPE ?? 'CHAR',
    display,
  } as ColumnDef
  const formColumns: string[] = []
  for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    for (const gc of g.COLUMNS || []) if (gc.NAME) formColumns.push(gc.NAME)
  }

  // ── Highlight with colour (the column's ADV_FORMAT) ───────────────────────
  const isNumericCol = (x: VisualColumn) =>
    /NUMBER|NUMERIC|DECIMAL|INT|FLOAT|DOUBLE|MONEY/i.test(String(x.COLTYPE || ''))
    || x.EXPRESSIONTYPE === 'G' || /BigDecimal|Integer|Long|Double/.test(String(x.JAVATYPE || ''))
  const ruleEngine = colourRuleEngine(catalogEntry.ID)
  const showColourRules = !!ruleEngine
    && (ruleEngine === 'grid' ? isNumericCol(c) : isValuesGroup)
  const ruleFields: { name: string; label: string; numeric: boolean }[] = []
  for (const g of model.LAYOUT_DATA.visualLayout.ColumnGroups || []) {
    for (const gc of g.COLUMNS || []) {
      if (!gc.NAME || gc.COLID === c.COLID || ruleFields.some((f) => f.name === gc.NAME)) continue
      ruleFields.push({ name: gc.NAME, label: gc.content || gc.NAME, numeric: isNumericCol(gc) })
    }
  }

  return (
    <div>
      <SectionHeader>Column</SectionHeader>
      <Field label="Display label">
        <input
          type="text"
          value={c.content || ''}
          onChange={(e) => onColumnFieldChange(c.COLID, 'content', e.target.value)}
          style={inputStyle}
        />
      </Field>

      {calcInfo ? (
        <Field label="Calculation">
          <div className="vdo-calc-sum">
            <code>{calcDef?.formula || calcInfo.formula}</code>
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {(calcDef?.when || calcInfo.when) === 'row' ? 'Row by row, then added up' : 'After totals'}
            </span>
            {calcBad && (
              <span className="vdo-cd-err" role="alert" style={{ margin: 0 }}>
                <AlertCircle size={14} aria-hidden="true" /><span>{calcBad} — it runs with its last working formula until fixed.</span>
              </span>
            )}
            {onEditCalc && (
              <button type="button" className="vdo-cd-op fn" style={{ alignSelf: 'flex-start', padding: '0 10px' }}
                onClick={() => onEditCalc(calcInfo.key)}>Edit calculation</button>
            )}
          </div>
        </Field>
      ) : isSchema ? (
        <>
          <Field label="Source">
            <input type="text" value={`${c.DBTABLE || ''}.${c.DBNAME || ''}`} readOnly style={{ ...inputStyle, opacity: 0.7, fontFamily: MONO_FONT }} />
          </Field>
          <Field label="Aggregation / function">
            <select
              value={c.FUNCTION || ''}
              onChange={(e) => {
                const fn = e.target.value
                const isWin = fnCatalog.some((f) => f.name === fn && f.kind === 'window')
                onColumnFieldChange(c.COLID, 'FUNCTION', fn)
                onColumnFieldChange(c.COLID, 'DEFAULTFUNCTION', fn)
                onColumnFieldChange(c.COLID, 'EXPRESSIONTYPE', fn ? 'G' : 'C')
                if (isWin) {
                  // A ranking column is numeric and needs the measure it
                  // ranks by — default to the first aggregated measure DESC.
                  onColumnFieldChange(c.COLID, 'COLTYPE', 'NUMBER')
                  onColumnFieldChange(c.COLID, 'type', 'number')
                  const hasSpec = Array.isArray(c.OVER_ORDER_BY) && (c.OVER_ORDER_BY as unknown[]).length > 0
                  if (!hasSpec) {
                    const measure = allModelColumns.find(
                      (m) => m.COLID !== c.COLID
                        && fnCatalog.some((f) => f.name === (m.FUNCTION || '').toUpperCase() && f.kind !== 'window'),
                    ) || allModelColumns.find((m) => m.COLID !== c.COLID)
                    if (measure) {
                      onColumnFieldChange(c.COLID, 'OVER_ORDER_BY',
                        [{ column: measure.NAME, direction: 'DESC' }])
                    }
                  }
                } else if (c.OVER_ORDER_BY) {
                  onColumnFieldChange(c.COLID, 'OVER_ORDER_BY', undefined)
                }
              }}
              style={selectStyle}
            >
              <option value="">(none — plain column)</option>
              <optgroup label="Aggregate">
                {fnCatalog.filter((f) => f.kind === 'aggregate').map((f) => (
                  <option key={f.name} value={f.name}>{f.label}</option>
                ))}
              </optgroup>
              {fnCatalog.some((f) => f.kind === 'statistical') && (
                <optgroup label="Statistical">
                  {fnCatalog.filter((f) => f.kind === 'statistical').map((f) => (
                    <option key={f.name} value={f.name}>{f.label}</option>
                  ))}
                </optgroup>
              )}
              {fnCatalog.some((f) => f.kind === 'window') && (
                <optgroup label="Ranking">
                  {fnCatalog.filter((f) => f.kind === 'window').map((f) => (
                    <option key={f.name} value={f.name}>{f.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </Field>
          {isWindowFn && (
            <Field label="Rank by">
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  value={rankBy?.column || ''}
                  onChange={(e) => onColumnFieldChange(c.COLID, 'OVER_ORDER_BY',
                    [{ column: e.target.value, direction: rankBy?.direction || 'DESC' }])}
                  style={{ ...selectStyle, flex: 1, minWidth: 0 }}
                >
                  {rankBy?.column && !allModelColumns.some((m) => m.NAME === rankBy.column) && (
                    <option value={rankBy.column}>{rankBy.column}</option>
                  )}
                  {allModelColumns.filter((m) => m.COLID !== c.COLID).map((m) => (
                    <option key={m.COLID} value={m.NAME}>{m.content || m.name || m.NAME}</option>
                  ))}
                </select>
                <select
                  value={rankBy?.direction || 'DESC'}
                  onChange={(e) => onColumnFieldChange(c.COLID, 'OVER_ORDER_BY',
                    [{ column: rankBy?.column || '', direction: e.target.value }])}
                  style={selectStyle}
                >
                  <option value="DESC">highest = 1</option>
                  <option value="ASC">lowest = 1</option>
                </select>
              </div>
            </Field>
          )}
          <Field label="Type">
            <select
              value={c.COLTYPE || 'CHAR'}
              onChange={(e) => {
                const ct = e.target.value
                onColumnFieldChange(c.COLID, 'COLTYPE', ct)
                onColumnFieldChange(c.COLID, 'type', ct === 'NUMBER' ? 'number' : ct === 'DATE' ? 'date' : 'string')
              }}
              style={selectStyle}
            >
              {COLTYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </>
      ) : (
        <>
          <Field label="SELECT alias">
            <input type="text" value={c.NAME || ''} readOnly style={{ ...inputStyle, opacity: 0.7, fontFamily: MONO_FONT }} />
          </Field>
          <Field label="Type">
            <select
              value={c.type || 'string'}
              onChange={(e) => {
                const t = e.target.value as 'string' | 'number' | 'date'
                onColumnFieldChange(c.COLID, 'type', t)
                onColumnFieldChange(c.COLID, 'COLTYPE', t === 'number' ? 'NUMBER' : t === 'date' ? 'DATE' : 'CHAR')
              }}
              style={selectStyle}
            >
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </>
      )}

      <Field label="Display format">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={display?.type && display.type !== 'text' ? display.type : 'Plain text'}
            readOnly
            style={{ ...inputStyle, opacity: 0.85, flex: 1 }}
          />
          <button type="button" onClick={() => setFormatOpen(true)} style={editBtnStyle}>Edit…</button>
        </div>
      </Field>

      {showPivotAgg && (
        <>
          <Field label="Pivot aggregation">
            <select
              value={pivotAgg}
              onChange={(e) => onColumnFieldChange(c.COLID, 'PIVOT_AGG', e.target.value || undefined)}
              style={selectStyle}
            >
              {PIVOT_AGG_OPTIONS.map((o) => <option key={o.value || '_none'} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          {showDirection && (
            <Field label="Direction">
              <select
                value={(c.PIVOT_AXIS as string) || 'columns'}
                onChange={(e) => onColumnFieldChange(c.COLID, 'PIVOT_AXIS', e.target.value)}
                style={selectStyle}
              >
                <option value="columns">Across columns</option>
                <option value="rows">Down rows</option>
              </select>
            </Field>
          )}
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '2px 2px 10px', lineHeight: 1.5 }}>
            Computed in the pivot from the fetched rows; the Aggregation above controls how the database groups rows.
          </div>
        </>
      )}

      {showColourRules && (
        <>
          <SectionHeader>Highlight with colour</SectionHeader>
          <ColourRulesEditor
            key={c.COLID}
            value={typeof c.ADV_FORMAT === 'string' ? c.ADV_FORMAT : ''}
            onChange={(adv) => onColumnFieldChange(c.COLID, 'ADV_FORMAT', adv)}
            measure={c.content || c.NAME}
            visualId={catalogEntry.ID}
            fields={ruleFields}
          />
        </>
      )}

      <Field label="Width">
        <input
          type="number"
          value={c.WIDTH ?? 100}
          onChange={(e) => onColumnFieldChange(c.COLID, 'WIDTH', Number(e.target.value))}
          style={inputStyle}
        />
      </Field>
      <Field label="Placement (group)">
        <select
          value={currentGroup || ''}
          onChange={(e) => onMoveColumn(c.COLID, e.target.value || null)}
          style={selectStyle}
        >
          <option value="">(remove from chart)</option>
          {catalogEntry.ColumnGroups.map((g) => (
            <option key={g.StandardName} value={g.StandardName}>
              {g.GroupName}{g.AllowedColumnTypes !== 'ANY' ? ` (${g.AllowedColumnTypes} only)` : ''}
            </option>
          ))}
        </select>
      </Field>

      <DisplayFormatEditor
        open={formatOpen}
        onClose={() => setFormatOpen(false)}
        column={formatColumn}
        formColumns={formColumns}
        onSave={(d) => onColumnFieldChange(c.COLID, 'display', d)}
      />
    </div>
  )
}

const editBtnStyle: React.CSSProperties = {
  padding: '5px 12px', fontSize: 13, borderRadius: 5, cursor: 'pointer', whiteSpace: 'nowrap',
  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
}

// ════════════════════════════════════════════════════════════════
// Small shared primitives
// ════════════════════════════════════════════════════════════════
/** Collapsible numbered-section header (Data Source tab). Collapsed, it keeps
 *  the picked value visible as a chip so the choice is never hidden. */
function SectionToggleHeader({
  label, badge, open, summary, onToggle,
}: {
  label: string
  badge?: React.ReactNode
  open: boolean
  summary?: string
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={open ? 'Collapse section' : 'Expand section'}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        border: 'none', background: 'transparent', padding: '2px 0',
        cursor: 'pointer', textAlign: 'left', minWidth: 0,
      }}
    >
      {open
        ? <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />
        : <ChevronRight size={14} style={{ flexShrink: 0, color: 'var(--color-text-secondary)' }} />}
      <span style={eyebrowStyle}>{label}</span>
      {badge}
      {!open && summary && <span style={summaryChipStyle}>{summary}</span>}
    </button>
  )
}

const summaryChipStyle: React.CSSProperties = {
  fontSize: 13.5, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
  background: 'var(--color-accent-weak)', color: 'var(--color-accent)',
  border: '1px solid color-mix(in srgb, var(--color-accent) 40%, transparent)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 340,
}

/** Expand-all / collapse-all icon pair for a folder tree header. */
function FolderExpandControls({ onExpandAll, onCollapseAll, expandTitle = 'Expand all folders', collapseTitle = 'Collapse all folders' }: {
  onExpandAll: () => void
  onCollapseAll: () => void
  expandTitle?: string
  collapseTitle?: string
}) {
  const btn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 22, height: 22, padding: 0, border: '1px solid var(--color-border)',
    borderRadius: 5, background: 'var(--color-bg)', cursor: 'pointer',
    color: 'var(--color-text-secondary)',
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
      <button type="button" onClick={onExpandAll} title={expandTitle} style={btn}>
        <ChevronsUpDown size={13} />
      </button>
      <button type="button" onClick={onCollapseAll} title={collapseTitle} style={btn}>
        <ChevronsDownUp size={13} />
      </button>
    </span>
  )
}

function ModeButton({ active, label, hint, onClick }: { active: boolean; label: string; hint: string; onClick: () => void }) {
  return (
    <button
      className="vdx-pickcard"
      onClick={onClick}
      style={{
        flex: 1,
        ...(active ? {
          background: 'var(--color-accent-weak)',
          borderColor: 'var(--color-accent)',
          boxShadow: 'inset 0 0 0 0.5px var(--color-accent)',
        } : undefined),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{label}</span>
        <CheckCircle active={active} />
      </div>
      <div style={{ fontSize: 14.5, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{hint}</div>
    </button>
  )
}

/** Business-area picker card (§8.3.3 area cards row). */
const AREA_DOT_COLORS = ['#4c8dff', '#3fb950', '#d29922', '#a371f7', '#db61a2', '#56d4dd']
function AreaCard({
  name, description, dotColor, active, tableCount, onPick,
}: {
  name: string
  description: string
  dotColor: string
  active: boolean
  tableCount?: number
  onPick: () => void
}) {
  return (
    <button
      className="vdx-pickcard"
      onClick={onPick}
      style={{
        width: 232, flexShrink: 0,
        ...(active ? {
          background: 'var(--color-accent-weak)',
          borderColor: 'var(--color-accent)',
          boxShadow: 'inset 0 0 0 0.5px var(--color-accent)',
        } : undefined),
      }}
      title={description}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, minWidth: 0 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{
          fontWeight: 700, fontSize: 15, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </span>
        <CheckCircle active={active} />
      </div>
      <div style={{
        fontSize: 14, color: 'var(--color-text-secondary)', lineHeight: 1.45,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        minHeight: 34,
      }}>
        {description}
      </div>
      {tableCount != null && (
        <div style={{ fontSize: 14, color: 'var(--color-text-faint)', marginTop: 6 }}>
          {tableCount} {tableCount === 1 ? 'table' : 'tables'}
        </div>
      )}
    </button>
  )
}

/** Right-aligned picked indicator shared by the choice cards. */
function CheckCircle({ active }: { active: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        marginLeft: 'auto', width: 17, height: 17, borderRadius: '50%', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, lineHeight: 1,
        background: active ? 'var(--color-accent)' : 'transparent',
        border: active ? 'none' : '1.5px solid var(--color-border-strong)',
        color: active ? '#fff' : 'transparent',
      }}
    >✓</span>
  )
}

/** Spec §0.3 toggle switch — 42×24 pill, accent when on. */
function SectionHeader({ children, actions }: { children: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <div style={{
      ...eyebrowStyle,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginTop: 4, marginBottom: 8,
    }}>
      <span style={{ minWidth: 0 }}>{children}</span>
      {actions}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
    </div>
  )
}

/** SQL / identifier font per spec §0.3. */
const MONO_FONT = "var(--font-mono, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace)"

// Hover affordances need real CSS classes (inline styles can't hover). Base
// borders live here so :hover wins; active/selected states override inline.
const VDX_CSS = `
.vdx-bankchip {
  display: flex; align-items: center; gap: 7px; padding: 7px 9px;
  border: 1px dashed var(--color-border-strong); border-radius: 7px;
  margin-bottom: 6px; background: var(--color-bg-secondary);
  color: var(--color-text); cursor: grab; user-select: none; min-width: 0;
}
.vdx-bankchip:hover { border-color: var(--color-accent); }
.vdx-colchip {
  display: flex; align-items: center; gap: 7px; padding: 7px 9px;
  border: 1px solid var(--color-border); border-radius: 7px;
  margin-bottom: 6px; background: var(--color-raised);
  color: var(--color-text); cursor: grab; user-select: none; min-width: 0;
}
.vdx-colchip:hover { border-color: var(--color-border-strong); }
.vdx-pickcard {
  text-align: left; padding: 13px 15px;
  border: 1px solid var(--color-border); border-radius: 12px;
  background: var(--color-bg-secondary); color: var(--color-text);
  cursor: pointer; font: inherit; min-width: 0;
}
.vdx-pickcard:hover { border-color: var(--color-border-strong); }
`

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 11px', fontSize: 15.5,
  border: '1px solid var(--color-border)', borderRadius: 8,
  background: 'var(--color-input-bg)', color: 'var(--color-text)',
  fontFamily: 'inherit',
}
const selectStyle: React.CSSProperties = { ...inputStyle }
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4,
}
const dropTargetStyle: React.CSSProperties = {
  border: '1.5px dashed var(--color-border-strong)', borderRadius: 11,
  background: 'var(--color-bg-secondary)',
}
/** Bordered surface panel (spec §0.3 cards/panels). */
const panelStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 11,
  background: 'var(--color-bg-secondary)',
}
const groupHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  gap: 8, marginBottom: 8,
}
/** Step helper copy under an eyebrow (spec 13px dim body). */
const helperStyle: React.CSSProperties = {
  fontSize: 15, color: 'var(--color-text-secondary)', marginTop: 5, lineHeight: 1.55,
}
const hintStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-secondary)', marginTop: 7, lineHeight: 1.55,
}
const emptyHintStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--color-text-faint)', fontStyle: 'italic', padding: '4px 6px',
}
const tokenChipStyle: React.CSSProperties = {
  fontSize: 13, fontFamily: MONO_FONT, padding: '2px 9px', borderRadius: 5,
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
  color: 'var(--color-text-secondary)',
}
/** Green "New" micro-badge next to the step-1 eyebrow (§8.3.3). */
const newBadgeStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
  padding: '1px 8px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0,
  background: 'var(--color-success-weak)', color: 'var(--color-success)',
}
